/**
 * Дашборд отдела маркетинга. Интеграция с Я.Директ (один кабинет на инсталляцию).
 *
 * Поток подключения:
 *   1. GET /api/marketing/oauth-status → получаем authorize_url
 *   2. Юзер открывает authorize_url, логинится в Я.Аккаунт, видит verification_code
 *   3. Юзер копирует код + указывает login кабинета → POST /api/marketing/oauth/connect
 *   4. После подключения — кнопка «Синхронизировать», тянет 30 дней статистики.
 */
import { useState, useEffect, useRef } from "react";
import type { CSSProperties, ReactNode, PointerEvent as ReactPointerEvent } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ResponsiveContainer, Bar, Area, ComposedChart, Line, Legend,
  XAxis, YAxis, Tooltip, CartesianGrid,
  ScatterChart, Scatter, ZAxis, ReferenceLine, Cell, PieChart, Pie,
} from "recharts";
import { Spinner } from "@/components/Spinner";
import type { MarketingZone, MarketingAccount, FunnelSegment, MarketingCampaignRow } from "@/api/dashboard";
import { useAuth, hasAccess } from "@/auth/AuthContext";
import {
  fetchMarketingOverview, fetchMarketingCampaigns, syncMarketing,
  fetchMarketingBreakdown, fetchBreakdownInsights, fetchMarketingInsights, fetchMarketingAccounts,
  fetchMarketingFunnel, fetchCardsConfig, saveCardsConfig,
  fetchInsightsHistory, fetchInsightVersion, fetchFunnelDeals, setExcludedLead, fetchSuccessDeals,
} from "@/api/dashboard";
import type { CustomCard, CustomChart, ChartSeries, CardsConfig, FunnelDeal } from "@/api/dashboard";


function fmtRub(v: number): string {
  return v.toLocaleString("ru", { maximumFractionDigits: 0 }) + " ₽";
}
function fmtInt(v: number): string {
  return v.toLocaleString("ru");
}
function fmtPct(v: number): string {
  return v.toFixed(2).replace(".", ",") + " %";
}
type Fmt = "int" | "num2" | "rub" | "pct";
function fmtByFormat(v: number, f: Fmt): string {
  return f === "rub" ? fmtRub(v) : f === "pct" ? fmtPct(v) : f === "num2" ? v.toFixed(2).replace(".", ",") : fmtInt(v);
}
// Реестр метрик для кастомных карточек: ключ → {название, значение, формат, источник}.
type Reg = Record<string, { label: string; value: number; fmt: Fmt; group: string }>;
function computeCustom(c: CustomCard, reg: Reg): string {
  const a = reg[c.a]?.value ?? 0;
  const b = c.b ? (reg[c.b]?.value ?? 0) : 0;
  let v = 0;
  if (c.type === "metric") v = a;
  else if (c.type === "ratio") v = b !== 0 ? (a / b) * 100 : 0;
  else if (c.type === "divide") v = b !== 0 ? a / b : 0;
  else if (c.type === "multiply") v = a * b;
  else if (c.type === "sum") v = a + b;
  else if (c.type === "diff") v = a - b;
  else if (c.type === "scale") v = a * (c.k ?? 1);
  return fmtByFormat(v, c.format);
}

// Зональная индикация 🟢🟡🔴 — сдержанный subtle-тинт под светлой TG-темой.
const ZONE_TINT: Record<MarketingZone, { bg: string; ink: string; dot: string } | null> = {
  green:  { bg: "#eaf6ee", ink: "#1f7a3f", dot: "#2e9e57" },
  yellow: { bg: "#fbf3e2", ink: "#8a6300", dot: "#d99a1c" },
  red:    { bg: "#fdeceb", ink: "#b23b30", dot: "#d35145" },
  nodata: null,
};
// Дельта vs прошлый период. inverse=true для метрик «меньше — лучше» (CPC/CPA/Расход).
function deltaView(pct: number | null | undefined, inverse = false): { text: string; color: string } | null {
  if (pct === null || pct === undefined) return null;
  const up = pct > 0;
  const arrow = pct === 0 ? "·" : up ? "↑" : "↓";
  const good = pct === 0 ? null : (inverse ? !up : up);
  const color = good === null ? "var(--mute)" : good ? "#2e9e57" : "#d35145";
  return { text: `${arrow} ${Math.abs(pct).toFixed(0)} %`, color };
}
// Курсор-кисть в нашем стиле (TG-зелёный line-SVG) — при активной палитре.
const BRUSH_CURSOR = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='22' height='22' viewBox='0 0 24 24' fill='none' stroke='%234fae4e' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M9.06 11.9l8.07-8.06a2.85 2.85 0 1 1 4.03 4.03l-8.06 8.08'/%3E%3Cpath d='M7.07 14.94c-1.66 0-3 1.35-3 3.02 0 1.33-2.5 1.52-2 2.02 1.08 1.1 2.49 2.02 4 2.02 2.2 0 4-1.8 4-4.04a3.01 3.01 0 0 0-3-3.02z'/%3E%3C/svg%3E\") 2 20, crosshair";

// База конверсии для этапа: клики Я.Директа / конверсии по целям Я.Директа /
// все сделки сегмента / любой предыдущий этап.
type GoalRow = { goal_id: number; name: string; conversions: number };
function funnelBaseOptions(seg: FunnelSegment, idx: number, clicks: number, goals: GoalRow[] = []): { key: string; label: string; count: number }[] {
  const opts = [
    { key: "clicks", label: "Клики Я.Директа", count: clicks },
    ...goals.map(g => ({ key: `ydgoal:${g.goal_id}`, label: `Конв · ${g.name} (Я.Директ)`, count: g.conversions })),
    { key: "total", label: "Все сделки сегмента", count: seg.total },
  ];
  for (let j = 0; j < idx; j++) opts.push({ key: `stage:${seg.steps[j].stage_id}`, label: seg.steps[j].name, count: seg.steps[j].count });
  return opts;
}
function funnelBaseKey(seg: FunnelSegment, idx: number, override?: string): string {
  return override ?? (idx > 0 ? `stage:${seg.steps[idx - 1].stage_id}` : "clicks");
}


export function MarketingPage() {
  const { user } = useAuth();
  if (!hasAccess(user, "marketing")) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--mute)" }}>
        Раздел «Маркетинг» доступен только участникам отдела маркетинга.
      </div>
    );
  }

  const accountsQ = useQuery({ queryKey: ["marketing/accounts"], queryFn: fetchMarketingAccounts });

  if (accountsQ.isLoading) return <div style={{ display: "flex", justifyContent: "center", padding: "28px 12px" }}><Spinner size="lg" label="Загрузка…"/></div>;
  const accounts = accountsQ.data ?? [];

  if (accounts.length === 0) return <NoAccounts/>;
  return <ConnectedView accounts={accounts}/>;
}

function NoAccounts() {
  return (
    <div style={{ padding: 40, maxWidth: 560, margin: "0 auto", textAlign: "center" }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Маркетинг — Я.Директ</h1>
      <p style={{ color: "var(--mute)", fontSize: 14, marginTop: 8, lineHeight: 1.5 }}>
        Кабинеты Я.Директа ещё не подключены. Добавьте один или несколько в Настройках → вкладка «Доступ».
      </p>
      <Link to="/settings" className="btn btn-sm btn-primary" style={{ marginTop: 12, display: "inline-block" }}>
        Перейти в Настройки → Доступ
      </Link>
    </div>
  );
}


function localISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const TRAFFIC_SOURCES: { source: string; label: string; wired: boolean }[] = [
  { source: "yandex_direct", label: "Яндекс.Директ", wired: true },
  { source: "vk_ads", label: "VK Реклама", wired: false },
  { source: "telegram_ads", label: "Telegram Ads", wired: false },
  { source: "avito", label: "Авито", wired: false },
  { source: "google_ads", label: "Google Ads", wired: false },
];

function ConnectedView({ accounts }: { accounts: MarketingAccount[] }) {
  const qc = useQueryClient();
  const [days, setDays] = useState(30);
  const [custom, setCustom] = useState<{ from: string; to: string } | null>(null);
  const [source, setSource] = useState("yandex_direct");   // выбранный источник трафика
  const [cabinet, setCabinet] = useState<number | null>(null);  // выбранный кабинет; null = Общий отчёт по всем
  const [periodOpen, setPeriodOpen] = useState(false);     // поповер периода в плавающем стеке
  const periodRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!periodOpen) return;
    const onDown = (e: MouseEvent) => { if (periodRef.current && !periodRef.current.contains(e.target as Node)) setPeriodOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setPeriodOpen(false); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); window.removeEventListener("keydown", onKey); };
  }, [periodOpen]);
  // Период: пресет (N дней) или свой диапазон дат. Фильтрует обзор/кампании/срезы/воронку.
  const presetTo = localISO(new Date());
  const presetFromDate = new Date(); presetFromDate.setDate(presetFromDate.getDate() - (days - 1));
  const presetFrom = localISO(presetFromDate);
  const range = custom ? { date_from: custom.from, date_to: custom.to } : { date_from: presetFrom, date_to: presetTo };
  const overviewQ = useQuery({
    queryKey: ["marketing/overview", range.date_from, range.date_to, cabinet],
    queryFn: () => fetchMarketingOverview({ ...range, account_id: cabinet ?? undefined }),
  });
  const campaignsQ = useQuery({
    queryKey: ["marketing/campaigns", range.date_from, range.date_to, cabinet],
    queryFn: () => fetchMarketingCampaigns({ ...range, account_id: cabinet ?? undefined }),
  });
  const funnelQ = useQuery({
    queryKey: ["marketing/funnel", range.date_from, range.date_to],
    queryFn: () => fetchMarketingFunnel(range),
  });
  const [crBaseFor, setCrBaseFor] = useState<Record<string, string>>({});
  const [crModal, setCrModal] = useState<{ si: number; idx: number } | null>(null);
  const [dealsModal, setDealsModal] = useState<{ stageId: number; name: string } | null>(null);
  const [chartModal, setChartModal] = useState<CustomChart | null>(null);
  const dealsQ = useQuery({
    queryKey: ["marketing/funnel-deals", dealsModal?.stageId, range.date_from, range.date_to],
    queryFn: () => fetchFunnelDeals(dealsModal!.stageId, range),
    enabled: dealsModal != null,
  });
  const exclMut = useMutation({
    mutationFn: ({ id, excluded }: { id: number; excluded: boolean }) => setExcludedLead(id, excluded),
    onSuccess: () => {
      ["marketing/funnel", "marketing/campaign-funnel", "marketing/funnel-deals", "marketing/contracts",
       "marketing/success-deals", "marketing/overview", "marketing/campaigns"].forEach(k => qc.invalidateQueries({ queryKey: [k] }));
    },
  });
  const handleToggleExclude = (id: number, excluded: boolean) => exclMut.mutate({ id, excluded });
  const syncMut = useMutation({
    mutationFn: () => syncMarketing({ date_from: range.date_from, date_to: range.date_to }),  // синк по выбранному периоду, все кабинеты
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketing/accounts"] });
      qc.invalidateQueries({ queryKey: ["marketing/overview"] });
      qc.invalidateQueries({ queryKey: ["marketing/campaigns"] });
      qc.invalidateQueries({ queryKey: ["marketing/campaign-funnel"] });
      qc.invalidateQueries({ queryKey: ["marketing/funnel"] });
    },
  });
  // Авто-ре-синк при смене периода (чтобы не жать кнопку — данные подтянутся сами).
  const firstPeriod = useRef(true);
  useEffect(() => {
    if (firstPeriod.current) { firstPeriod.current = false; return; }
    syncMut.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.date_from, range.date_to]);

  const ov = overviewQ.data;
  const camps = campaignsQ.data?.campaigns ?? [];
  const convGoals = campaignsQ.data?.goals ?? [];
  // Кастомные метрики (созданные в таблице), которые считаются из ДНЕВНЫХ полей — их можно строить на графике.
  const DAILY_ALLOWED = new Set(["impressions", "clicks", "conversions", "ctr", "cpc", "cpa", "cr", "cost"]);
  const dailyKeyOf = (k: string) => (k === "cost_rub" ? "cost" : k === "avg_cpc_rub" ? "cpc" : k === "cpa_rub" ? "cpa" : k);
  const loadCC = (sk: string): CustomCol[] => { try { return (JSON.parse(localStorage.getItem(sk) || "[]") as any[]).map(migrateCol); } catch { return []; } };
  const dashCustomCols = (() => { const seen = new Set<string>(); const out: CustomCol[] = []; for (const cc of [...loadCC("mkt_cols_analytics_customcols"), ...loadCC("mkt_cols_dash_customcols")]) { if (!seen.has(cc.id)) { seen.add(cc.id); out.push(cc); } } return out; })();
  const dailyCustom = dashCustomCols.filter(cc => cc.tokens.every(t => t.t !== "m" || DAILY_ALLOWED.has(dailyKeyOf(t.k))));
  const evalDaily = (day: any, tokens: FToken[]): number => {
    if (!tokens.length) return 0;
    const val = (t: FToken) => t.t === "m" ? Number(day[dailyKeyOf(t.k)] ?? 0) : t.t === "n" ? t.v : 0;
    let acc = val(tokens[0]);
    for (let i = 1; i + 1 < tokens.length; i += 2) { const op = tokens[i], rhs = tokens[i + 1]; if (op.t !== "o") break; const v = val(rhs); acc = op.o === "+" ? acc + v : op.o === "-" ? acc - v : op.o === "*" ? acc * v : (v ? acc / v : 0); }
    return acc;
  };
  const chartData = (ov?.daily ?? []).map(d => {
    const base: any = {
      d: d.date.slice(5).split("-").reverse().join("."), impressions: d.impressions, clicks: d.clicks,
      cost: d.cost_rub, conversions: d.conversions,
      ctr: d.impressions ? d.clicks / d.impressions * 100 : 0,
      cpc: d.clicks ? d.cost_rub / d.clicks : 0,
      cpa: d.conversions ? d.cost_rub / d.conversions : 0,
      cr: d.clicks ? d.conversions / d.clicks * 100 : 0,
    };
    dailyCustom.forEach(cc => { base[`cc:${cc.id}`] = evalDaily(base, cc.tokens); });
    return base;
  });
  // Реестр метрик графиков: ключ → {label (рус), формат}.
  const CHART_METRICS: { key: string; label: string; fmt: Fmt }[] = [
    { key: "impressions", label: "Показы", fmt: "int" }, { key: "clicks", label: "Клики", fmt: "int" },
    { key: "cost", label: "Расход ₽", fmt: "rub" }, { key: "conversions", label: "Конверсии", fmt: "int" },
    { key: "ctr", label: "CTR %", fmt: "pct" }, { key: "cpc", label: "CPC ₽", fmt: "rub" },
    { key: "cpa", label: "CPA ₽", fmt: "rub" }, { key: "cr", label: "CR %", fmt: "pct" },
    ...dailyCustom.map(cc => ({ key: `cc:${cc.id}`, label: cc.label, fmt: cc.fmt })),
  ];
  const chartMeta = (key: string): { label: string; fmt: Fmt } => { const m = CHART_METRICS.find(x => x.key === key); return m ? { label: m.label, fmt: m.fmt } : { label: key, fmt: "int" }; };
  // Стандартные графики (новый формат: серии) + примеры новых видов.
  const stdCharts: CustomChart[] = [
    { id: "chart:imp", title: "Показы", type: "area", series: [{ key: "impressions" }] },
    { id: "chart:clicks", title: "Клики", type: "line", series: [{ key: "clicks" }] },
    { id: "chart:cost", title: "Расход ₽", type: "bar", series: [{ key: "cost" }] },
    { id: "chart:conv", title: "Конверсии", type: "area", series: [{ key: "conversions" }] },
    { id: "chart:combo", title: "Расход + Конверсии (комбо)", type: "combo", series: [{ key: "cost", as: "bar", axis: "L", color: "#2459b8" }, { key: "conversions", as: "line", axis: "R", color: "#4fae4e" }] },
    { id: "chart:multi", title: "CTR и CR (мульти)", type: "line", series: [{ key: "ctr", color: "#e0883a" }, { key: "cr", color: "#7a6bff" }] },
  ];
  const chartView = (c: CustomChart): CustomChart => (c.series && c.type) ? c
    : { ...c, type: (c.kind === "bar" ? "bar" : "area"), series: [{ key: c.metric || "clicks" }] };
  const lastSync = accounts
    .map(a => a.last_sync_at).filter(Boolean)
    .sort().slice(-1)[0];
  const syncErrors = accounts.filter(a => a.last_sync_error);

  // --- Конструктор карточек: видимость + кастомные карточки ---
  const { user } = useAuth();
  const isOwner = !!(user && (user.is_superuser || user.role === "owner"));
  const cardsQ = useQuery({ queryKey: ["marketing/cards"], queryFn: fetchCardsConfig });
  const [showCards, setShowCards] = useState(false);
  const cfg: CardsConfig = cardsQ.data ?? { hidden: [], custom: [], charts_hidden: [], charts_custom: [] };
  const hidden = new Set(cfg.hidden);
  const customCards = cfg.custom;
  const chartsHidden = new Set(cfg.charts_hidden);
  const chartsCustom = cfg.charts_custom;
  const vis = (k: string) => !hidden.has(k);
  const chartVis = (k: string) => !chartsHidden.has(k);
  const saveCardsMut = useMutation({
    mutationFn: (c: CardsConfig) => saveCardsConfig(c),
    onSuccess: (d) => qc.setQueryData(["marketing/cards"], d),
  });
  const persist = (patch: Partial<CardsConfig>) => saveCardsMut.mutate({ ...cfg, ...patch });
  const toggleHide = (k: string) => { const h = new Set(hidden); h.has(k) ? h.delete(k) : h.add(k); persist({ hidden: [...h] }); };
  const toggleChart = (k: string) => { const h = new Set(chartsHidden); h.has(k) ? h.delete(k) : h.add(k); persist({ charts_hidden: [...h] }); };

  // Реестр метрик (операнды для кастомных карточек) + список для управления видимостью.
  const YD = "Я.Директ", CRM = "CRM-воронка (AmoCRM)";
  const reg: Reg = {};
  const manageList: { key: string; label: string; group: string }[] = [];
  if (ov) {
    reg["imp"] = { label: "Показы", value: ov.totals.impressions, fmt: "int", group: YD };
    reg["clicks"] = { label: "Клики", value: ov.totals.clicks, fmt: "int", group: YD };
    reg["cost"] = { label: "Расход с НДС", value: ov.totals.cost_rub, fmt: "rub", group: YD };
    reg["conv"] = { label: "Конверсии (итого)", value: ov.totals.conversions, fmt: "int", group: YD };
    reg["ctr"] = { label: "CTR", value: ov.totals.ctr, fmt: "pct", group: YD };
    reg["cpc"] = { label: "Средний CPC", value: ov.totals.avg_cpc_rub, fmt: "rub", group: YD };
    reg["cr"] = { label: "CR", value: ov.totals.cr, fmt: "pct", group: YD };
    reg["cpa"] = { label: "CPA", value: ov.totals.cpa_rub, fmt: "rub", group: YD };
    reg["bounce"] = { label: "% отказов", value: ov.totals.bounce_rate ?? 0, fmt: "pct", group: YD };
    reg["pageviews"] = { label: "Глубина (стр.)", value: ov.totals.avg_pageviews ?? 0, fmt: "num2", group: YD };
    reg["sessions"] = { label: "Визиты", value: ov.totals.sessions ?? 0, fmt: "int", group: YD };
    (ov.goals ?? []).forEach(g => { reg[`goal:${g.goal_id}`] = { label: `Конв · ${g.name}`, value: g.conversions, fmt: "int", group: YD }; });
    manageList.push({ key: "imp", label: "Показы", group: YD }, { key: "clicks", label: "Клики", group: YD }, { key: "cost", label: "Расход с НДС", group: YD });
    if (ov.goals && ov.goals.length > 0) {
      ov.goals.forEach(g => manageList.push({ key: `goal:${g.goal_id}`, label: `Конв · ${g.name}`, group: YD }));
      if (ov.goals.length > 1) manageList.push({ key: "conv_total", label: "Конверсии · итого", group: YD });
    } else manageList.push({ key: "conv", label: "Конверсии", group: YD });
    manageList.push({ key: "cpa", label: "CPA", group: YD });
    manageList.push({ key: "ctr", label: "CTR", group: YD }, { key: "cr", label: "CR", group: YD }, { key: "cpc", label: "Средний CPC", group: YD });
    manageList.push({ key: "bounce", label: "% отказов", group: YD }, { key: "pageviews", label: "Глубина просмотра", group: YD }, { key: "sessions", label: "Визиты", group: YD });
  }
  if (funnelQ.data?.configured) {
    const multi = funnelQ.data.segments.length > 1;
    funnelQ.data.segments.forEach((seg, si) => {
      const pref = multi ? `${seg.name} · ` : "";
      reg[`seg:${si}:total`] = { label: `${pref}Все сделки`, value: seg.total, fmt: "int", group: CRM };
      seg.steps.forEach(st => {
        reg[`seg:${si}:${st.stage_id}`] = { label: `${pref}${st.name}`, value: st.count, fmt: "int", group: CRM };
        manageList.push({ key: `fcount:${si}:${st.stage_id}`, label: `${pref}${st.name}`, group: CRM });
        manageList.push({ key: `fcpa:${si}:${st.stage_id}`, label: `CPA · ${st.name}`, group: CRM });
        manageList.push({ key: `fcr:${si}:${st.stage_id}`, label: `CR в ${st.name}`, group: CRM });
      });
    });
  }

  return (
    <div style={{ padding: 16, maxWidth: 1280, margin: "0 auto" }}>
      <style>{`
        .mkt-kpi { transition: box-shadow .15s ease, transform .15s ease; }
        .mkt-kpi:hover { box-shadow: 0 5px 16px rgba(20,20,20,0.09); transform: translateY(-1px); }
        .mkt-chart-card { transition: box-shadow .15s ease; }
        .mkt-chart-card:hover { box-shadow: 0 5px 18px rgba(20,20,20,0.08); }
        .card-title { margin-top: 0 !important; margin-bottom: 12px !important; }
        @keyframes mkt-spin { to { transform: rotate(360deg); } }
        .mkt-spin { animation: mkt-spin .8s linear infinite; }
        .mkt-fab { transition: transform .12s ease, box-shadow .12s ease; }
        .mkt-fab:hover { transform: translateY(-1px); }
        @keyframes mkt-fade { from { opacity: 0; } }
        @keyframes mkt-pop { from { opacity: 0; transform: translateY(6px); } }
        .mkt-modal-backdrop { animation: mkt-fade .18s ease; }
        .mkt-modal { animation: mkt-pop .2s ease-out; }
        .mkt-modal-x { transition: background .12s ease, color .12s ease; }
        .mkt-modal-x:hover { background: var(--paper-2, #f1f1f4); color: var(--ink); }
      `}</style>
      {/* Под-шапка: источники + кабинеты — чипсы без подложки, только нижний разделитель */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
        <span style={{ flex: "0 0 70px", fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.5 }}>Источник</span>
        {TRAFFIC_SOURCES.map(s => (
          <button key={s.source} disabled={!s.wired} onClick={() => s.wired && setSource(s.source)}
                  className={`btn btn-sm ${source === s.source ? "btn-primary" : ""}`}
                  title={s.wired ? s.label : "Источник не подключён"}
                  style={{ opacity: s.wired ? 1 : 0.45, cursor: s.wired ? "pointer" : "not-allowed" }}>
            {s.label}{!s.wired && " · не подключено"}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 16, paddingBottom: 14, borderBottom: "1px solid var(--line)" }}>
        <span style={{ flex: "0 0 70px", fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.5 }}>Кабинет</span>
        <button onClick={() => setCabinet(null)} className={`btn btn-sm ${cabinet === null ? "btn-primary" : ""}`}
                title="Сводные показатели по всем кабинетам">Общий отчёт по всем</button>
        {accounts.map(a => (
          <button key={a.id} onClick={() => setCabinet(a.id)} className={`btn btn-sm ${cabinet === a.id ? "btn-primary" : ""}`}>
            {a.title ? `${a.title} · ${a.login}` : (a.login || `Кабинет ${a.id}`)}
          </button>
        ))}
        <Link to="/settings" style={{ color: "var(--brick)", fontSize: 12, marginLeft: 4 }}>управление</Link>
      </div>
      <div style={{ marginBottom: 14 }}>
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>
          {cabinet === null ? "Общий отчёт — все кабинеты" : (() => {
            const a = accounts.find(x => x.id === cabinet);
            return a ? (a.title ? `${a.title} · ${a.login}` : a.login) : "Кабинет";
          })()}
        </h1>
        <div style={{ marginTop: 4, fontSize: 12.5, color: "var(--mute)" }}>
          {TRAFFIC_SOURCES.find(s => s.source === source)?.label} · период {custom ? `${range.date_from}…${range.date_to}` : `${days} дн.`}
          {lastSync && <span> · синк: {new Date(lastSync).toLocaleString("ru")}</span>}
          {syncErrors.length > 0 && <span style={{ color: "var(--brick)" }}> · ошибок: {syncErrors.length}</span>}
        </div>
      </div>

      {/* Плавающий стек контролов справа снизу (виден при прокрутке) */}
      <div style={{ position: "fixed", right: 16, bottom: 18, zIndex: 35, display: "flex", flexDirection: "column", gap: 10 }}>
        <button className="mkt-fab" onClick={() => syncMut.mutate()} disabled={syncMut.isPending} title="Синхронизировать все"
                style={{ width: 48, height: 48, borderRadius: 14, border: "none", background: "var(--brick)", color: "#fff",
                         cursor: "pointer", display: "grid", placeItems: "center", boxShadow: "0 4px 12px rgba(79,174,78,0.4)" }}>
          <svg className={syncMut.isPending ? "mkt-spin" : ""} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12a9 9 0 1 1-2.64-6.36"/><path d="M21 3v6h-6"/>
          </svg>
        </button>
        {isOwner && (
          <button className="mkt-fab" onClick={() => setShowCards(true)} title="Карточки (показатели/графики)"
                  style={{ width: 48, height: 48, borderRadius: 14, border: "1px solid var(--line)", background: "var(--card)", color: "var(--ink-2)",
                           cursor: "pointer", display: "grid", placeItems: "center", boxShadow: "0 2px 8px rgba(20,20,20,0.1)" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/>
              <rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>
            </svg>
          </button>
        )}
        <div ref={periodRef} style={{ position: "relative" }}>
          <button className="mkt-fab" onClick={() => setPeriodOpen(o => !o)} title="Период"
                  style={{ width: 48, height: 48, borderRadius: 14, border: "1px solid var(--line)", background: "var(--card)", color: "var(--ink-2)",
                           cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1,
                           boxShadow: "0 2px 8px rgba(20,20,20,0.1)" }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
            </svg>
            <span style={{ fontSize: 9, fontWeight: 800, color: "var(--ink)" }}>{custom ? "свой" : `${days}д`}</span>
          </button>
          {periodOpen && (
              <div style={{ position: "absolute", right: "calc(100% + 10px)", bottom: 0, zIndex: 36, width: 190, background: "var(--card)",
                            border: "1px solid var(--line)", borderRadius: 12, padding: 10, boxShadow: "0 6px 20px rgba(0,0,0,0.16)" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>Период</div>
                {[7, 14, 30, 60, 90].map(d => (
                  <button key={d} className={`btn btn-sm ${!custom && days === d ? "btn-primary" : ""}`}
                          onClick={() => { setCustom(null); setDays(d); setPeriodOpen(false); }}
                          style={{ width: "100%", justifyContent: "flex-start", marginBottom: 4, background: !custom && days === d ? undefined : "var(--card)" }}>
                    {d} дней
                  </button>
                ))}
                <button className={`btn btn-sm ${custom ? "btn-primary" : ""}`} onClick={() => setCustom({ from: presetFrom, to: presetTo })}
                        style={{ width: "100%", justifyContent: "flex-start", background: custom ? undefined : "var(--card)" }}>
                  Свой период
                </button>
                {custom && (
                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                    <input className="input-sm" type="date" value={custom.from} max={custom.to}
                           onChange={(e) => setCustom({ ...custom, from: e.target.value })}/>
                    <input className="input-sm" type="date" value={custom.to} min={custom.from} max={presetTo}
                           onChange={(e) => setCustom({ ...custom, to: e.target.value })}/>
                  </div>
                )}
              </div>
          )}
        </div>
      </div>

      {syncMut.isError && (
        <div style={{ marginTop: 12, padding: 10, background: "#fee", borderRadius: 6, fontSize: 13, color: "var(--brick)" }}>
          Синк не удался: {(syncMut.error as Error).message}
        </div>
      )}

      <InsightsPanel range={range}/>

      {/* KPI блок */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginTop: 20 }}>
        {vis("imp") && <Kpi label="Показы"   value={fmtInt(ov?.totals.impressions ?? 0)} delta={deltaView(ov?.deltas?.impressions)}/>}
        {vis("clicks") && <Kpi label="Клики"  value={fmtInt(ov?.totals.clicks ?? 0)} delta={deltaView(ov?.deltas?.clicks)}/>}
        {vis("cost") && <Kpi label="Расход с НДС" value={fmtRub(ov?.totals.cost_rub ?? 0)} delta={deltaView(ov?.deltas?.cost_rub, true)} accent/>}
        {ov?.goals && ov.goals.length > 0 ? (
          <>
            {ov.goals.filter(g => vis(`goal:${g.goal_id}`)).map(g => (
              <Kpi key={`goal${g.goal_id}`} label={`Конв · ${g.name}`} value={fmtInt(g.conversions)} delta={deltaView(g.delta)}/>
            ))}
            {ov.goals.length > 1 && vis("conv_total") && (
              <Kpi label="Конверсии · итого" value={fmtInt(ov.goals.reduce((s, g) => s + g.conversions, 0))}
                   delta={deltaView(ov?.deltas?.conversions)}/>
            )}
          </>
        ) : (
          vis("conv") && <Kpi label="Конверсии" value={fmtInt(ov?.totals.conversions ?? 0)} delta={deltaView(ov?.deltas?.conversions)}/>
        )}
        {vis("cpa") && <Kpi label="CPA" value={(ov?.totals.conversions ?? 0) > 0 ? fmtRub(ov?.totals.cpa_rub ?? 0) : "—"} zone={ov?.zones?.cpa} delta={deltaView(ov?.deltas?.cpa_rub, true)}/>}

        {/* Карточки по этапам воронки — сразу после CPA, config-driven */}
        {funnelQ.data?.configured && funnelQ.data.segments.flatMap((seg, si) => {
          const multi = funnelQ.data!.segments.length > 1;
          return seg.steps.flatMap((st, idx) => {
            const opts = funnelBaseOptions(seg, idx, ov?.totals.clicks ?? 0, ov?.goals ?? []);
            const bkey = funnelBaseKey(seg, idx, crBaseFor[`${si}:${st.stage_id}`]);
            const base = opts.find(o => o.key === bkey) ?? opts[0];
            const cr = base.count > 0 ? st.count / base.count * 100 : null;
            const pref = multi ? `${seg.name} · ` : "";
            return [
              vis(`fcount:${si}:${st.stage_id}`)
                ? (idx === seg.steps.length - 1
                    ? <Kpi key={`fc${si}-${st.stage_id}`} label={`${pref}${st.name}`} value={fmtInt(st.count)}/>
                    : <ClickableKpi key={`fc${si}-${st.stage_id}`} label={`${pref}${st.name}`} value={fmtInt(st.count)}
                            sub="нажми — сделки" onClick={() => setDealsModal({ stageId: st.stage_id, name: st.name })}/>)
                : null,
              vis(`fcpa:${si}:${st.stage_id}`) ? <Kpi key={`fp${si}-${st.stage_id}`} label={`CPA · ${st.name}`} value={st.cost_per_rub != null ? fmtRub(st.cost_per_rub) : "—"}/> : null,
              vis(`fcr:${si}:${st.stage_id}`) ? <ClickableKpi key={`fr${si}-${st.stage_id}`} label={`CR в ${st.name}`} value={cr != null ? fmtPct(cr) : "—"}
                            sub={`от: ${base.label}`} onClick={() => setCrModal({ si, idx })}/> : null,
            ];
          });
        })}

        {vis("ctr") && <Kpi label="CTR" value={fmtPct(ov?.totals.ctr ?? 0)} zone={ov?.zones?.ctr} delta={deltaView(ov?.deltas?.ctr)}/>}
        {vis("cr") && <Kpi label="CR"   value={fmtPct(ov?.totals.cr ?? 0)} zone={ov?.zones?.cr} delta={deltaView(ov?.deltas?.cr)}/>}
        {vis("cpc") && <Kpi label="Средний CPC" value={fmtRub(ov?.totals.avg_cpc_rub ?? 0)} zone={ov?.zones?.cpc} delta={deltaView(ov?.deltas?.avg_cpc_rub, true)}/>}
        {vis("bounce") && <Kpi label="% отказов" value={fmtPct(ov?.totals.bounce_rate ?? 0)}/>}
        {vis("pageviews") && <Kpi label="Глубина просмотра" value={`${(ov?.totals.avg_pageviews ?? 0).toFixed(2).replace(".", ",")} стр.`}/>}
        {vis("sessions") && <Kpi label="Визиты" value={fmtInt(ov?.totals.sessions ?? 0)}/>}

        {/* Кастомные карточки пользователя */}
        {customCards.map(c => (
          <Kpi key={c.id} label={c.title} value={computeCustom(c, reg)}/>
        ))}
      </div>

      <FunnelBlock range={range}/>

      {/* Отдельный раздел: успешные сделки по настроенным success-этапам + исключение из расчётов */}
      <ContractsSection range={range} onToggle={handleToggleExclude} busy={exclMut.isPending}/>

      {/* Динамика по дням — каждый график отдельным блоком (крупно), с видимостью + кастомные */}
      {chartData.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Динамика по дням</h3>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(440px, 1fr))", gap: 16 }}>
            {stdCharts.filter(c => chartVis(c.id)).map(c => (
              <ChartCard key={c.id} chart={c} data={chartData} meta={chartMeta} onExpand={() => setChartModal(c)} onHide={() => toggleChart(c.id)}/>
            ))}
            {chartsCustom.map(c => { const cv = chartView(c); return (
              <ChartCard key={cv.id} chart={cv} data={chartData} meta={chartMeta} onExpand={() => setChartModal(cv)} onHide={() => toggleChart(cv.id)}/>
            ); })}
          </div>
          {chartsHidden.size > 0 && (
            <button className="btn btn-sm" style={{ marginTop: 10 }}
                    onClick={() => persist({ charts_hidden: [] })}>Показать скрытые графики ({chartsHidden.size})</button>
          )}
        </div>
      )}

      {/* Тепловая карта по дням недели (из дневных данных) */}
      {ov?.daily && ov.daily.length > 0 && chartVis("chart:heatmap") && <WeekdayHeatmap daily={ov.daily} onHide={() => toggleChart("chart:heatmap")}/>}

      {/* Таблица кампаний с поиском и настройкой столбцов */}
      <CampaignsTable campaigns={camps} loading={campaignsQ.isLoading} storageKey="mkt_cols_dash" title="Кампании" convGoals={convGoals}
        headerExtra={<Link to={"/analytics" as never} className="btn btn-sm btn-primary" style={{ textDecoration: "none" }}>Подробная аналитика →</Link>}/>

      {crModal && funnelQ.data?.segments?.[crModal.si] && (() => {
        const seg = funnelQ.data.segments[crModal.si];
        const st = seg.steps[crModal.idx];
        const opts = funnelBaseOptions(seg, crModal.idx, ov?.totals.clicks ?? 0, ov?.goals ?? []);
        const curKey = funnelBaseKey(seg, crModal.idx, crBaseFor[`${crModal.si}:${st.stage_id}`]);
        return (
          <Modal title={`CR в «${st.name}» — от чего считать?`} onClose={() => setCrModal(null)}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {opts.map(o => {
                const active = o.key === curKey;
                const pct = o.count > 0 ? st.count / o.count * 100 : null;
                return (
                  <button key={o.key} onClick={() => { setCrBaseFor({ ...crBaseFor, [`${crModal.si}:${st.stage_id}`]: o.key }); setCrModal(null); }}
                          style={{ textAlign: "left", padding: "10px 12px", borderRadius: 8, cursor: "pointer", fontSize: 13,
                                   border: active ? "1px solid var(--brick)" : "1px solid var(--line)",
                                   background: active ? "rgba(79,174,78,0.08)" : "var(--card)" }}>
                    <strong>{o.label}</strong> <span style={{ color: "var(--mute)" }}>· {fmtInt(o.count)}</span>
                    {pct != null && <span style={{ float: "right", color: "var(--ink-2)" }}>{fmtPct(pct)}</span>}
                  </button>
                );
              })}
            </div>
          </Modal>
        );
      })()}

      {chartModal && (
        <Modal title={chartModal.title} onClose={() => setChartModal(null)} width={1100}>
          <div style={{ width: "100%" }}>
            <ChartView chart={chartModal} data={chartData} meta={chartMeta} height={Math.round(window.innerHeight * 0.6)}/>
            <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 8 }}>
              По дням за период {range.date_from} — {range.date_to}. Наведи на график — значения за день.
            </div>
          </div>
        </Modal>
      )}

      {dealsModal && (
        <Modal title={`Сделки на этапе «${dealsModal.name}»`} onClose={() => setDealsModal(null)} width={960}>
          <div style={{ width: "100%" }}>
            {dealsQ.isFetching ? <div style={{ display: "flex", justifyContent: "center", padding: "28px 12px" }}><Spinner label="Загрузка…"/></div> : dealsQ.data?.error ? (
              <div style={{ color: "var(--brick)", fontSize: 12.5 }}>{dealsQ.data.error}</div>
            ) : !dealsQ.data?.deals?.length ? (
              <div style={{ color: "var(--mute)", fontSize: 13, padding: 16, textAlign: "center" }}>Нет сделок за период.</div>
            ) : (
              <DealsList deals={dealsQ.data.deals} onToggle={handleToggleExclude} busy={exclMut.isPending}/>
            )}
          </div>
        </Modal>
      )}

      {showCards && (
        <CardsManagerModal manageList={manageList} hidden={hidden} custom={customCards} reg={reg}
          chartList={stdCharts.map(c => ({ key: c.id, label: c.title }))} chartsHidden={chartsHidden}
          chartsCustom={chartsCustom} chartMetrics={CHART_METRICS}
          onToggle={toggleHide} onToggleChart={toggleChart}
          onSaveCustom={(next) => persist({ custom: next })}
          onSaveCharts={(next) => persist({ charts_custom: next })}
          onClose={() => setShowCards(false)}/>
      )}
    </div>
  );
}


function ClickableKpi({ label, value, sub, onClick }: { label: string; value: string; sub?: string; onClick: () => void }) {
  return (
    <button onClick={onClick} title="Выбрать базу конверсии"
            style={{ textAlign: "left", padding: "12px 14px", background: "#fff", border: "1px solid var(--line)", borderRadius: 10, cursor: "pointer", position: "relative" }}>
      <div style={{ fontSize: 10.5, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.5, display: "flex", justifyContent: "space-between", gap: 4 }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        <span style={{ color: "var(--brick)" }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" ><path d="M4 7h8M16 7h4M4 17h4M12 17h8M14 4v6M8 14v6"/></svg></span>
      </div>
      <div style={{ fontSize: 19, fontWeight: 700, marginTop: 4, fontFamily: "var(--serif)", letterSpacing: "-0.01em", color: "var(--ink)" }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, color: "var(--mute)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</div>}
    </button>
  );
}

function Modal({ title, children, onClose, width = 480 }: { title: string; children: ReactNode; onClose: () => void; width?: number }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div onClick={onClose} className="mkt-modal-backdrop"
         style={{ position: "fixed", inset: 0, background: "rgba(17,19,24,0.32)", backdropFilter: "blur(3px)", WebkitBackdropFilter: "blur(3px)",
                  display: "grid", placeItems: "center", zIndex: 1000, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} className="mkt-modal"
           style={{ maxWidth: width, width: "100%", padding: 24, maxHeight: "88vh", overflowY: "auto", background: "var(--card)",
                    borderRadius: 16, boxShadow: "0 16px 48px rgba(0,0,0,0.16)", border: "1px solid var(--line)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 1.3 }}>{title}</h3>
          <button onClick={onClose} aria-label="Закрыть" className="mkt-modal-x"
                  style={{ flex: "0 0 auto", width: 32, height: 32, borderRadius: 10, border: "none", background: "transparent", color: "var(--mute)", cursor: "pointer", display: "grid", placeItems: "center" }}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function FunnelBlock({ range }: { range: { date_from: string; date_to: string } }) {
  const q = useQuery({
    queryKey: ["marketing/funnel", range.date_from, range.date_to],
    queryFn: () => fetchMarketingFunnel(range),
  });
  const data = q.data;

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 className="card-title" style={{ margin: 0 }}>Воронка продаж (сделки из CRM)</h3>
      <div style={{ marginTop: 12 }}>
        {q.isLoading ? <div style={{ display: "flex", justifyContent: "center", padding: "28px 12px" }}><Spinner label="Загрузка…"/></div> : data?.error ? (
          <div style={{ color: "var(--brick)", fontSize: 12.5, padding: 10 }}>Не удалось: {data.error}</div>
        ) : data && !data.configured ? (
          <div style={{ color: "var(--mute)", fontSize: 13, padding: 10 }}>
            Сегменты не настроены. Зайдите в Настройки → «Сделки и воронка» и задайте UTM-поля + этапы.
          </div>
        ) : !data?.segments?.length ? (
          <div style={{ color: "var(--mute)", padding: 16, textAlign: "center" }}>Нет данных за период.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ fontSize: 11.5, color: "var(--mute)" }}>
              Расход Я.Директа за период: <strong style={{ color: "var(--ink)" }}>{fmtRub(data.spend_rub ?? 0)}</strong>. CPO/цена считаются по нему.
            </div>
            {data.segments.map((seg, i) => <FunnelSegmentView key={i} seg={seg}/>)}
          </div>
        )}
      </div>
    </div>
  );
}

function FunnelSegmentView({ seg }: { seg: FunnelSegment }) {
  const maxCount = Math.max(1, ...seg.steps.map(s => s.count));
  return (
    <div style={{ border: "1px solid var(--line)", borderRadius: 10, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <span style={{ fontWeight: 700, fontSize: 15 }}>{seg.name}</span>
        <span style={{ fontSize: 12.5, color: "var(--mute)" }}>
          Сделок: <strong style={{ color: "var(--ink)" }}>{fmtInt(seg.total)}</strong>
          {" · "}Продаж: <strong style={{ color: "var(--ink)" }}>{fmtInt(seg.won)}</strong>
          {seg.conv_total_won != null && <> · Конверсия в продажу: <strong style={{ color: "var(--ink)" }}>{seg.conv_total_won}%</strong></>}
          {seg.cpo_rub != null && <> · CPO: <strong style={{ color: "var(--ink)" }}>{fmtRub(seg.cpo_rub)}</strong></>}
        </span>
      </div>
      {seg.steps.length === 0 ? (
        <div style={{ fontSize: 12.5, color: "var(--mute)" }}>Этапы для этого сегмента не выбраны.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {seg.steps.map((st, i) => (
            <div key={st.stage_id} style={{ display: "grid", gridTemplateColumns: "minmax(120px, 200px) 1fr auto", gap: 10, alignItems: "center" }}>
              <span style={{ fontSize: 12.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                             borderLeft: st.color ? `3px solid ${st.color}` : undefined, paddingLeft: st.color ? 8 : 0 }}>{st.name}</span>
              <div style={{ background: "var(--paper-2, #f4f4f5)", borderRadius: 6, height: 22, position: "relative", overflow: "hidden" }}>
                <div style={{ width: `${(st.count / maxCount) * 100}%`, height: "100%", background: "var(--brick)", borderRadius: 6, minWidth: st.count > 0 ? 24 : 0 }}/>
                <span style={{ position: "absolute", left: 8, top: 0, lineHeight: "22px", fontSize: 11.5, fontWeight: 700,
                               color: st.count > 0 ? "#fff" : "var(--mute)", textShadow: st.count > 0 ? "0 1px 2px rgba(0,0,0,0.25)" : undefined }}>
                  {fmtInt(st.count)}
                </span>
              </div>
              <span style={{ fontSize: 11.5, color: "var(--mute)", whiteSpace: "nowrap", textAlign: "right", minWidth: 130 }}>
                {i > 0 && st.conv_from_prev != null ? `${st.conv_from_prev}% от пред.` : "—"}
                {st.cost_per_rub != null && <> · {fmtRub(st.cost_per_rub)}</>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// Маркеры информационных/нецелевых запросов — кандидаты в минус-слова.
const MINUS_MARKERS = [
  "как ", "что такое", "своими руками", "бесплатн", "отзыв", "википед",
  "реферат", "фото", "ваканси", "скачать", "форум", "что это", "сделать сам",
  "инструкци", "своими", "видео", "чертеж", "схема",
];
// Известный мусор площадок РСЯ.
const JUNK_PLACEMENTS = ["games", "game", "igr", "dsp", "appodeal", "mobile app", "vpn", "proxy"];

function isMinusCandidate(label: string, cost: number, conv: number): boolean {
  const l = label.toLowerCase();
  if (MINUS_MARKERS.some(m => l.includes(m))) return true;
  return cost > 500 && conv === 0;   // открутил >500₽ без конверсий
}
function isJunkPlacement(label: string, cost: number, conv: number, cpa: number): boolean {
  const l = label.toLowerCase();
  if (JUNK_PLACEMENTS.some(m => l.includes(m))) return true;
  if (cost > 500 && conv === 0) return true;
  return cpa > 0 && cpa > 2500;       // аномально дорогая площадка
}

const CLEANUP_TABS: { dim: string; label: string }[] = [
  { dim: "query", label: "Поисковые запросы" },
  { dim: "placement", label: "Площадки РСЯ" },
];

// AI-оценка одного среза (срезы аудитории / чистка трафика). Запрос по кнопке (Claude-вызов платный).
function BreakdownAiPanel({ dim, range, kind }: {
  dim: string; range: { date_from: string; date_to: string }; kind: "audience" | "cleanup";
}) {
  const qc = useQueryClient();
  const [show, setShow] = useState(false);
  const qkey = ["marketing/breakdown-insights", dim, range.date_from, range.date_to];
  const q = useQuery({ queryKey: qkey, queryFn: () => fetchBreakdownInsights(dim, range), enabled: show });
  const refresh = useMutation({
    mutationFn: () => fetchBreakdownInsights(dim, range, true),
    onSuccess: d => qc.setQueryData(qkey, d),
  });
  const data = q.data;
  const busy = q.isFetching || refresh.isPending;
  const title = kind === "cleanup" ? "AI-оценка чистки" : "AI-оценка среза";
  return (
    <div style={{ marginBottom: 14, border: "1px solid #d9eedd", borderRadius: 12, background: "linear-gradient(180deg,#f6fbf6,#fdfffd)", padding: "12px 14px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 7, fontWeight: 700, fontSize: 13 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--brick)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9z"/><path d="M19 14l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z"/>
          </svg>
          {title}
        </span>
        {!show ? (
          <button className="btn btn-sm btn-primary" onClick={() => setShow(true)}>Оценить через ИИ</button>
        ) : (
          <button className="btn btn-sm" disabled={busy} onClick={() => refresh.mutate()}>{busy ? "Анализирую…" : "Обновить"}</button>
        )}
      </div>
      {show && (
        <div style={{ marginTop: 10 }}>
          {busy && !data ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "20px 12px" }}><Spinner label="Анализирую данные…"/></div>
          ) : data?.error ? (
            <div style={{ color: "var(--brick)", fontSize: 12.5 }}>{data.error}</div>
          ) : data ? (
            <>
              {data.summary && (
                <div style={{ fontSize: 13.5, lineHeight: 1.5, fontFamily: "var(--serif)", marginBottom: 12 }}>{data.summary}</div>
              )}
              {!!data.observations?.length && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Наблюдения</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {data.observations.map((o, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, fontSize: 12.5, lineHeight: 1.45 }}>
                        <span style={{ flex: "0 0 auto", width: 18, height: 18, borderRadius: "50%", background: "#eef2f5", color: "var(--ink-2)", display: "grid", placeItems: "center", fontSize: 10, fontWeight: 700 }}>{i + 1}</span>
                        <span>{o}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!!data.recommendations?.length && (
                <div>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--brick)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>
                    {kind === "cleanup" ? "Что почистить" : "Рекомендации по ставкам"}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {data.recommendations.map((r, i) => (
                      <div key={i} style={{ display: "flex", gap: 8, fontSize: 12.5, lineHeight: 1.45, padding: "7px 10px", borderRadius: 9, background: "#fff", border: "1px solid #e3eee5" }}>
                        <span style={{ color: "var(--brick)", fontWeight: 800, flex: "0 0 auto" }}>→</span><span>{r}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {data.cached && <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 8 }}>из кэша</div>}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}

function CleanupPanel({ range }: { range: { date_from: string; date_to: string } }) {
  const [open, setOpen] = useState(false);
  const [dim, setDim] = useState("query");
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const q = useQuery({
    queryKey: ["marketing/cleanup", dim, range.date_from, range.date_to],
    queryFn: () => fetchMarketingBreakdown(dim, range),
    enabled: open,
  });
  const data = q.data;
  const rows = (data?.rows ?? [])
    .map(r => {
      const flagged = dim === "query"
        ? isMinusCandidate(r.label, r.cost_rub, r.conversions)
        : isJunkPlacement(r.label, r.cost_rub, r.conversions, r.cpa_rub);
      return { ...r, flagged };
    })
    .filter(r => !onlyFlagged || r.flagged)
    .slice(0, 100);
  const flaggedCount = (data?.rows ?? []).filter(r => dim === "query"
    ? isMinusCandidate(r.label, r.cost_rub, r.conversions)
    : isJunkPlacement(r.label, r.cost_rub, r.conversions, r.cpa_rub)).length;

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
           onClick={() => setOpen(o => !o)}>
        <h3 className="card-title" style={{ margin: 0 }}>Чистка трафика</h3>
        <span style={{ color: "var(--mute)", fontSize: 13 }}>{open ? "Свернуть" : "Показать"}</span>
      </div>

      {open && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
            {CLEANUP_TABS.map(t => (
              <button key={t.dim} className={`btn btn-sm ${dim === t.dim ? "btn-primary" : ""}`}
                      onClick={() => setDim(t.dim)}>{t.label}</button>
            ))}
            <label style={{ display: "flex", gap: 5, alignItems: "center", fontSize: 12.5, color: "var(--ink-3)", marginLeft: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={onlyFlagged} onChange={e => setOnlyFlagged(e.target.checked)}/>
              Только кандидаты на чистку{data?.rows ? ` (${flaggedCount})` : ""}
            </label>
          </div>

          <BreakdownAiPanel dim={dim} range={range} kind="cleanup"/>

          {q.isLoading ? <div style={{ display: "flex", justifyContent: "center", padding: "28px 12px" }}><Spinner label="Загрузка…"/></div> : data?.error ? (
            <div style={{ color: "var(--brick)", fontSize: 12.5, padding: 10 }}>Не удалось: {data.error}</div>
          ) : !rows.length ? (
            <div style={{ color: "var(--mute)", padding: 16, textAlign: "center" }}>Нет данных за период.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="tbl tbl-tight">
                <thead>
                  <tr>
                    <th>{dim === "query" ? "Запрос" : "Площадка"}</th>
                    <th className="num">Клики</th>
                    <th className="num">Конв.</th>
                    <th className="num">Расход</th>
                    <th className="num">CPA</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.key}>
                      <td style={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label}</td>
                      <td className="num tabular">{fmtInt(r.clicks)}</td>
                      <td className="num tabular">{fmtInt(r.conversions)}</td>
                      <td className="num tabular strong">{fmtRub(r.cost_rub)}</td>
                      <td className="num tabular">{r.conversions > 0 ? fmtRub(r.cpa_rub) : "—"}</td>
                      <td>{r.flagged && (
                        <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: ZONE_TINT.red!.bg, color: ZONE_TINT.red!.ink }}>
                          ⚠ чистка
                        </span>
                      )}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize: 11.5, color: "var(--mute)", marginTop: 8 }}>
                Кандидаты помечаются эвристикой (информационные/нецелевые маркеры, расход без конверсий, аномальный CPA). Минус-слова добавляются в Поиск, минус-площадки — в РСЯ, на стороне Директа.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


const VERDICT_STYLE: Record<string, { bg: string; ink: string; label: string }> = {
  scale: { bg: ZONE_TINT.green!.bg, ink: ZONE_TINT.green!.ink, label: "Масштаб" },
  keep:  { bg: "#f3f3f3", ink: "var(--ink-2)", label: "Оставить" },
  watch: { bg: ZONE_TINT.yellow!.bg, ink: ZONE_TINT.yellow!.ink, label: "Наблюдать" },
  kill:  { bg: ZONE_TINT.red!.bg, ink: ZONE_TINT.red!.ink, label: "Отключить" },
};

function CardsManagerModal({ manageList, hidden, custom, reg, chartList, chartsHidden, chartsCustom, chartMetrics, onToggle, onToggleChart, onSaveCustom, onSaveCharts, onClose }: {
  manageList: { key: string; label: string; group: string }[];
  hidden: Set<string>;
  custom: CustomCard[];
  reg: Reg;
  chartList: { key: string; label: string }[];
  chartsHidden: Set<string>;
  chartsCustom: CustomChart[];
  chartMetrics: { key: string; label: string }[];
  onToggle: (k: string) => void;
  onToggleChart: (k: string) => void;
  onSaveCustom: (next: CustomCard[]) => void;
  onSaveCharts: (next: CustomChart[]) => void;
  onClose: () => void;
}) {
  const regKeys = Object.keys(reg);
  const [tab, setTab] = useState<"cards" | "charts">("cards");
  const [edit, setEdit] = useState<CustomCard | null>(null);
  const [chEdit, setChEdit] = useState<CustomChart | null>(null);
  const toSeries = (c: CustomChart): CustomChart => (c.series && c.type) ? { ...c, series: c.series.map(s => ({ ...s })) }
    : { id: c.id, title: c.title, type: (c.kind === "bar" ? "bar" : "area"), series: [{ key: c.metric || chartMetrics[0]?.key || "clicks" }] };
  const startCh = (c?: CustomChart) => setChEdit(c ? toSeries(c)
    : { id: "", title: "", type: "area", series: [{ key: chartMetrics[0]?.key ?? "clicks" }] });
  const setSeries = (next: ChartSeries[]) => setChEdit(e => e ? { ...e, series: next } : e);
  const saveCh = () => {
    if (!chEdit || !(chEdit.series?.length)) return;
    const ch: CustomChart = { ...chEdit, id: chEdit.id || `ch${Date.now()}`, title: chEdit.title.trim() || (chartMetrics.find(m => m.key === chEdit.series![0].key)?.label ?? "График") };
    onSaveCharts(chartsCustom.some(x => x.id === ch.id) ? chartsCustom.map(x => x.id === ch.id ? ch : x) : [...chartsCustom, ch]);
    setChEdit(null);
  };
  const start = (c?: CustomCard) => setEdit(c ? { ...c }
    : { id: "", title: "", type: "ratio", a: regKeys[0] ?? "", b: regKeys[1] ?? regKeys[0] ?? "", k: 1, format: "pct" });
  const TYPES: [CustomCard["type"], string][] = [
    ["metric", "Метрика как есть (A)"],
    ["divide", "Деление A ÷ B"],
    ["ratio", "Доля/конверсия A ÷ B × 100 (%)"],
    ["multiply", "Умножение A × B"],
    ["sum", "Сумма A + B"],
    ["diff", "Разность A − B"],
    ["scale", "A × множитель (константа)"],
  ];
  const FMTS: [CustomCard["format"], string][] = [["int", "Число"], ["num2", "Число (2 знака)"], ["rub", "Рубли ₽"], ["pct", "Проценты %"]];
  const needB = !!edit && ["ratio", "divide", "multiply", "sum", "diff"].includes(edit.type);
  const regGroups = Array.from(new Set(Object.values(reg).map(r => r.group)));
  const renderMetricSelect = (val: string, on: (v: string) => void) => (
    <select className="input-sm" value={val} onChange={e => on(e.target.value)}>
      {regGroups.map(gr => (
        <optgroup key={gr} label={gr}>
          {Object.keys(reg).filter(k => reg[k].group === gr).map(k => <option key={k} value={k}>{reg[k].label}</option>)}
        </optgroup>
      ))}
    </select>
  );
  const save = () => {
    if (!edit) return;
    const card: CustomCard = { ...edit, id: edit.id || `c${Date.now()}`, title: edit.title.trim() || "Карточка" };
    onSaveCustom(custom.some(c => c.id === card.id) ? custom.map(c => c.id === card.id ? card : c) : [...custom, card]);
    setEdit(null);
  };

  const groupsInList = Array.from(new Set(manageList.map(m => m.group)));

  return (
    <Modal title="Карточки и графики" onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 340, maxWidth: 480 }}>
        <div style={{ display: "flex", gap: 6 }}>
          <button className={`btn btn-sm ${tab === "cards" ? "btn-primary" : ""}`} onClick={() => setTab("cards")}>Карточки</button>
          <button className={`btn btn-sm ${tab === "charts" ? "btn-primary" : ""}`} onClick={() => setTab("charts")}>Графики</button>
        </div>

        {tab === "cards" && (<>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Видимость карточек</div>
            {groupsInList.map(gr => (
              <div key={gr} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 5 }}>{gr}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  {manageList.filter(m => m.group === gr).map(m => (
                    <label key={m.key} style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 12.5, cursor: "pointer" }}>
                      <input type="checkbox" checked={!hidden.has(m.key)} onChange={() => onToggle(m.key)}/>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Свои карточки</div>
              {!edit && <button className="btn btn-sm btn-primary" onClick={() => start()}>+ Добавить</button>}
            </div>
            {custom.length === 0 && !edit && (
              <div style={{ color: "var(--mute)", fontSize: 12 }}>Любая формула из метрик: деление, умножение, +, −, доля %, или метрика как есть. Напр. «CR в договор» = Договор ÷ Клики × 100 (%).</div>
            )}
            {!edit && custom.map(c => (
              <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: "1px solid var(--line)" }}>
                <span style={{ fontSize: 13, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  <strong>{c.title}</strong> <span style={{ color: "var(--mute)" }}>= {computeCustom(c, reg)}</span>
                </span>
                <span style={{ display: "flex", gap: 6, flex: "0 0 auto" }}>
                  <button className="btn btn-sm" onClick={() => start(c)}>✎</button>
                  <button className="btn btn-sm" onClick={() => onSaveCustom(custom.filter(x => x.id !== c.id))}>✕</button>
                </span>
              </div>
            ))}
            {edit && (
              <div style={{ border: "1px solid var(--line)", borderRadius: 8, padding: 12, marginTop: 8, display: "flex", flexDirection: "column", gap: 9 }}>
                <input className="input-sm" placeholder="Название карточки" value={edit.title} onChange={e => setEdit({ ...edit, title: e.target.value })}/>
                <label style={{ fontSize: 11, color: "var(--mute)" }}>Операция</label>
                <select className="input-sm" value={edit.type} onChange={e => setEdit({ ...edit, type: e.target.value as CustomCard["type"] })}>
                  {TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <label style={{ fontSize: 11, color: "var(--mute)" }}>Метрика A</label>
                {renderMetricSelect(edit.a, v => setEdit({ ...edit, a: v }))}
                {needB && (<>
                  <label style={{ fontSize: 11, color: "var(--mute)" }}>Метрика B</label>
                  {renderMetricSelect(edit.b ?? "", v => setEdit({ ...edit, b: v }))}
                </>)}
                {edit.type === "scale" && (
                  <input className="input-sm" type="number" placeholder="Множитель" value={edit.k ?? 1} onChange={e => setEdit({ ...edit, k: Number(e.target.value) })}/>
                )}
                <label style={{ fontSize: 11, color: "var(--mute)" }}>Формат значения</label>
                <select className="input-sm" value={edit.format} onChange={e => setEdit({ ...edit, format: e.target.value as CustomCard["format"] })}>
                  {FMTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <div style={{ fontSize: 12.5, color: "var(--ink-2)" }}>Предпросмотр: <strong>{computeCustom({ ...edit, id: "preview" }, reg)}</strong></div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn btn-sm btn-primary" onClick={save}>Сохранить</button>
                  <button className="btn btn-sm" onClick={() => setEdit(null)}>Отмена</button>
                </div>
              </div>
            )}
          </div>
        </>)}

        {tab === "charts" && (<>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Видимость графиков · Я.Директ</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {chartList.map(c => (
                <label key={c.key} style={{ display: "flex", gap: 7, alignItems: "center", fontSize: 12.5, cursor: "pointer" }}>
                  <input type="checkbox" checked={!chartsHidden.has(c.key)} onChange={() => onToggleChart(c.key)}/>
                  <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>Свои графики</div>
              {!chEdit && <button className="btn btn-sm btn-primary" onClick={() => startCh()}>+ Добавить</button>}
            </div>
            {chartsCustom.length === 0 && !chEdit && (
              <div style={{ color: "var(--mute)", fontSize: 12 }}>Свой график: тип (линия/площадь/столбцы/комбо с 2 осями) + несколько метрик, включая созданные.</div>
            )}
            {!chEdit && chartsCustom.map(c => {
              const cv = c.series && c.type ? c : { ...c, type: (c.kind === "bar" ? "bar" : "area") as any, series: [{ key: c.metric || "clicks" }] };
              return (
              <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: "1px solid var(--line)" }}>
                <span style={{ fontSize: 13, minWidth: 0 }}><strong>{c.title}</strong> <span style={{ color: "var(--mute)" }}>· {CHART_TYPE_LABEL[cv.type!]} · {(cv.series ?? []).map(s => chartMetrics.find(m => m.key === s.key)?.label ?? s.key).join(", ")}</span></span>
                <span style={{ display: "flex", gap: 6, flex: "0 0 auto" }}>
                  <button className="btn btn-sm" onClick={() => startCh(c)}>✎</button>
                  <button className="btn btn-sm" onClick={() => onSaveCharts(chartsCustom.filter(x => x.id !== c.id))}>✕</button>
                </span>
              </div>
            ); })}
            {chEdit && (
              <div style={{ border: "1px solid var(--line)", borderRadius: 10, padding: 14, marginTop: 8, display: "flex", flexDirection: "column", gap: 10 }}>
                <input className="input-sm" placeholder="Название графика" value={chEdit.title} onChange={e => setChEdit({ ...chEdit, title: e.target.value })}/>
                <div>
                  <label style={{ fontSize: 11, color: "var(--mute)", display: "block", marginBottom: 4 }}>Тип</label>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {(["line", "area", "bar", "combo"] as const).map(t => (
                      <button key={t} className={`btn btn-sm ${(chEdit.type ?? "area") === t ? "btn-primary" : ""}`} onClick={() => setChEdit({ ...chEdit, type: t })}>{CHART_TYPE_LABEL[t]}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 11, color: "var(--mute)", display: "block", marginBottom: 4 }}>Метрики {chEdit.type === "combo" && "· для каждой выбери вид и ось (L/R)"}</label>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {(chEdit.series ?? []).map((s, i) => (
                      <div key={i} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <input type="color" value={s.color || CHART_COLORS[i % CHART_COLORS.length]} title="Цвет"
                               onChange={e => setSeries((chEdit.series ?? []).map((x, j) => j === i ? { ...x, color: e.target.value } : x))}
                               style={{ width: 28, height: 28, padding: 0, border: "1px solid var(--line)", borderRadius: 6, flex: "0 0 auto", cursor: "pointer" }}/>
                        <select className="input-sm" value={s.key} onChange={e => setSeries((chEdit.series ?? []).map((x, j) => j === i ? { ...x, key: e.target.value } : x))} style={{ flex: 1, minWidth: 0 }}>
                          {chartMetrics.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                        </select>
                        {chEdit.type === "combo" && (<>
                          <select className="input-sm" value={s.as || "line"} onChange={e => setSeries((chEdit.series ?? []).map((x, j) => j === i ? { ...x, as: e.target.value as "line" | "bar" } : x))} style={{ flex: "0 0 80px" }}>
                            <option value="line">линия</option><option value="bar">столбцы</option>
                          </select>
                          <select className="input-sm" value={s.axis || "L"} onChange={e => setSeries((chEdit.series ?? []).map((x, j) => j === i ? { ...x, axis: e.target.value as "L" | "R" } : x))} style={{ flex: "0 0 64px" }} title="Ось">
                            <option value="L">ось L</option><option value="R">ось R</option>
                          </select>
                        </>)}
                        {(chEdit.series ?? []).length > 1 && <button className="btn btn-sm" onClick={() => setSeries((chEdit.series ?? []).filter((_, j) => j !== i))} style={{ flex: "0 0 auto" }}>✕</button>}
                      </div>
                    ))}
                  </div>
                  <button className="btn btn-sm" onClick={() => setSeries([...(chEdit.series ?? []), { key: chartMetrics[0]?.key ?? "clicks" }])} style={{ marginTop: 6 }}>+ метрика</button>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="btn btn-sm btn-primary" onClick={saveCh}>Сохранить</button>
                  <button className="btn btn-sm" onClick={() => setChEdit(null)}>Отмена</button>
                </div>
              </div>
            )}
          </div>
        </>)}
      </div>
    </Modal>
  );
}

function DealsList({ deals, onToggle, busy }: { deals: FunnelDeal[]; onToggle: (id: number, excluded: boolean) => void; busy?: boolean }) {
  const [ovr, setOvr] = useState<Record<number, boolean>>({});
  const isExcl = (d: FunnelDeal) => ovr[d.id] ?? !!d.excluded;
  const toggle = (d: FunnelDeal) => { const next = !isExcl(d); setOvr(o => ({ ...o, [d.id]: next })); onToggle(d.id, next); };
  const counted = deals.filter(d => !isExcl(d)).length;
  return (
    <>
      <div style={{ fontSize: 12.5, color: "var(--mute)", marginBottom: 12 }}>
        Учтено: <strong style={{ color: "var(--ink)" }}>{counted}</strong>
        {deals.length > counted ? ` · не учтено ${deals.length - counted}` : ""}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: "62vh", overflowY: "auto" }}>
        {deals.map((d, i) => {
          const excl = isExcl(d);
          return (
            <div key={d.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
                                     padding: "11px 14px", borderRadius: 12, background: "var(--card)", border: "1px solid var(--line)",
                                     boxShadow: "0 1px 2px rgba(20,20,20,0.04)", opacity: excl ? 0.55 : 1, transition: "opacity .15s" }}>
              <div style={{ display: "flex", gap: 11, alignItems: "center", minWidth: 0 }}>
                <div style={{ width: 26, height: 26, borderRadius: 8, background: "var(--paper-2, #f1f1f4)", display: "grid",
                              placeItems: "center", fontSize: 11, fontWeight: 700, color: "var(--mute)", flex: "0 0 auto" }}>{i + 1}</div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                textDecoration: excl ? "line-through" : "none" }}>{d.name}</div>
                  <div style={{ fontSize: 11.5, color: "var(--mute)", marginTop: 3, display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
                    {d.created_at ? <span>создана {new Date(d.created_at * 1000).toLocaleDateString("ru")}</span> : null}
                    {d.status_name ? <span style={{ padding: "1px 7px", borderRadius: 8, background: "var(--paper-2, #f1f1f4)", fontWeight: 600 }}>{d.status_name}</span> : null}
                    {d.utm_source ? <span>· {d.utm_source}</span> : null}
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "0 0 auto" }}>
                <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 9px", borderRadius: 10,
                               background: excl ? "#f0f0f3" : "#eaf6ee", color: excl ? "#8a8a92" : "#1f7a3f" }}>
                  {excl ? "Не учтена" : "Учтена"}
                </span>
                {d.price ? <span style={{ fontWeight: 800, fontSize: 14 }}>{fmtRub(d.price)}</span> : null}
                {d.amo_url ? <a className="btn btn-sm" href={d.amo_url} target="_blank" rel="noopener" style={{ textDecoration: "none" }}>→ Amo</a> : null}
                <button className="btn btn-sm" disabled={busy} onClick={() => toggle(d)}
                        title={excl ? "Вернуть в расчёты" : "Не учитывать в расчётах"}
                        style={{ fontWeight: 600, color: excl ? "#1f7a3f" : "#b23b30", borderColor: excl ? "#bfe3c9" : undefined }}>
                  {excl ? "Учитывать" : "Не учитывать"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function ContractsSection({ range, onToggle, busy }: {
  range: { date_from: string; date_to: string };
  onToggle: (id: number, excluded: boolean) => void; busy?: boolean;
}) {
  const q = useQuery({
    queryKey: ["marketing/success-deals", range.date_from, range.date_to],
    queryFn: () => fetchSuccessDeals(range),
  });
  const groups = q.data?.groups ?? [];
  const total = q.data?.total ?? 0;
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 className="card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="var(--brick)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "0 0 auto" }}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M9 13l2 2 4-4"/>
        </svg>
        Успешные сделки за период{q.data?.configured ? ` · ${total}` : ""}
      </h3>
      {q.isFetching ? <div style={{ display: "flex", justifyContent: "center", padding: "28px 12px" }}><Spinner label="Загрузка…"/></div> : q.data?.error ? (
        <div style={{ color: "var(--brick)", fontSize: 12.5 }}>{q.data.error}</div>
      ) : !q.data?.configured ? (
        <div style={{ color: "var(--mute)", fontSize: 13, padding: 12 }}>
          Этапы «Успешная сделка» не настроены. Задай их в Настройках → «Успешные сделки».
        </div>
      ) : total === 0 ? (
        <div style={{ color: "var(--mute)", fontSize: 13, padding: 12, textAlign: "center" }}>Успешных сделок за период нет.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {groups.map(g => (
            <div key={g.stage_id}>
              <div style={{ fontWeight: 700, fontSize: 13.5, marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                {g.stage_name}
                <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 10, background: "#eaf6ee", color: "#1f7a3f" }}>{g.deals.length}</span>
              </div>
              {g.deals.length === 0 ? (
                <div style={{ color: "var(--mute)", fontSize: 12, paddingLeft: 2 }}>— нет</div>
              ) : (
                <DealsList deals={g.deals} onToggle={onToggle} busy={busy}/>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InsightsPanel({ range }: { range: { date_from: string; date_to: string } }) {
  const qc = useQueryClient();
  const [versionId, setVersionId] = useState<number | null>(null);  // null = последняя версия
  const q = useQuery({
    queryKey: ["marketing/insights", "latest"],
    queryFn: () => fetchMarketingInsights(range),
    staleTime: 60 * 60 * 1000,
  });
  const histQ = useQuery({ queryKey: ["marketing/insights-history"], queryFn: fetchInsightsHistory });
  const verQ = useQuery({
    queryKey: ["marketing/insights-version", versionId],
    queryFn: () => fetchInsightVersion(versionId as number),
    enabled: versionId != null,
  });
  const refreshMut = useMutation({
    mutationFn: () => fetchMarketingInsights({ ...range, force: true }),
    onSuccess: (d) => {
      setVersionId(null);
      qc.setQueryData(["marketing/insights", "latest"], d);
      qc.invalidateQueries({ queryKey: ["marketing/insights-history"] });
    },
  });
  const data = refreshMut.isPending ? undefined : (versionId != null ? verQ.data : (refreshMut.data ?? q.data));
  const busy = (versionId == null && q.isFetching && !refreshMut.data) || refreshMut.isPending || (versionId != null && verQ.isFetching);
  const versions = histQ.data?.versions ?? [];
  const fmtTs = (s?: string) => s ? new Date(s.endsWith("Z") || s.includes("+") ? s : s + "Z").toLocaleString("ru", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h3 className="card-title" style={{ margin: 0 }}>AI-инсайты — что масштабировать / отключать</h3>
      </div>

      <div style={{ marginTop: 12 }}>
        {busy ? <div style={{ display: "flex", justifyContent: "center", padding: "28px 12px" }}><Spinner label="Загрузка…"/></div> : data?.error ? (
          <div style={{ color: "var(--brick)", fontSize: 12.5, padding: 10 }}>{data.error}</div>
        ) : data ? (
          <>
            {data.summary && (
              <div style={{ fontSize: 14.5, lineHeight: 1.55, marginBottom: 16, fontFamily: "var(--serif)", color: "var(--ink)",
                            background: "linear-gradient(180deg, #f3faf3, #fafffa)", border: "1px solid #d9eedd",
                            borderLeft: "4px solid var(--brick)", borderRadius: 12, padding: "14px 16px" }}>
                {data.summary}
              </div>
            )}
            {!!data.observations?.length && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Наблюдения</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {data.observations.map((o, i) => (
                    <div key={i} style={{ display: "flex", gap: 10, fontSize: 13, lineHeight: 1.5 }}>
                      <span style={{ flex: "0 0 auto", width: 20, height: 20, borderRadius: "50%", background: "#eef2f5", color: "var(--ink-2)",
                                     display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700, marginTop: 1 }}>{i + 1}</span>
                      <span>{o}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!!data.verdicts?.length && (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Вердикты по кампаниям</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                  {data.verdicts.map((v, i) => {
                    const s = VERDICT_STYLE[v.verdict] ?? VERDICT_STYLE.keep;
                    return (
                      <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, lineHeight: 1.45,
                                            padding: "9px 11px", borderRadius: 10, border: "1px solid var(--line)", background: "#fff" }}>
                        <span style={{ flex: "0 0 auto", width: 92, textAlign: "center", fontSize: 10.5, fontWeight: 700, padding: "4px 0", borderRadius: 8, background: s.bg, color: s.ink, textTransform: "uppercase", letterSpacing: 0.3 }}>{s.label}</span>
                        <span style={{ minWidth: 0 }}><strong>{v.campaign}</strong> <span style={{ color: "var(--ink-2)" }}>— {v.reason}</span></span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {versions.length > 0 && (
                <select className="input-sm" style={{ maxWidth: 280 }} value={versionId ?? ""}
                        onChange={e => setVersionId(e.target.value ? Number(e.target.value) : null)}>
                  <option value="">Последняя версия</option>
                  {versions.map(v => (
                    <option key={v.id} value={v.id}>
                      {fmtTs(v.created_at)} · {v.trigger === "daily" ? "авто 10:00" : "вручную"}
                    </option>
                  ))}
                </select>
              )}
              <button className="btn btn-sm btn-primary" disabled={refreshMut.isPending} onClick={() => refreshMut.mutate()}>
                {refreshMut.isPending ? "Обновляю…" : "Обновить анализ"}
              </button>
              <span style={{ fontSize: 11, color: "var(--mute)" }}>
                {data.created_at ? `${fmtTs(data.created_at)} · ` : ""}{data.trigger === "daily" ? "авто 10:00 МСК" : data.trigger === "manual" ? "вручную" : ""}{data.model ? ` · ${data.model}` : ""}
                {" · "}автообновление раз в день в 10:00 МСК
              </span>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}


const BREAKDOWN_TABS: { dim: string; label: string }[] = [
  { dim: "device", label: "Устройства" },
  { dim: "region", label: "Регионы" },
  { dim: "gender", label: "Пол" },
  { dim: "age", label: "Возраст" },
];

function BreakdownPanel({ range }: { range: { date_from: string; date_to: string } }) {
  const [open, setOpen] = useState(false);
  const [dim, setDim] = useState("device");
  const brkQ = useQuery({
    queryKey: ["marketing/breakdown", dim, range.date_from, range.date_to],
    queryFn: () => fetchMarketingBreakdown(dim, range),
    enabled: open,
  });
  const data = brkQ.data;
  // Эвристика: для устройств — флаг «мобилка дороже десктопа в ≥1.5×» по CPA.
  let mobileAlert: string | null = null;
  if (dim === "device" && data?.rows) {
    const m = data.rows.find(r => r.key === "MOBILE");
    const d = data.rows.find(r => r.key === "DESKTOP");
    if (m && d && d.cpa_rub > 0 && m.cpa_rub >= d.cpa_rub * 1.5) {
      mobileAlert = `⚠ Мобильные дороже десктопа: CPA ${fmtRub(m.cpa_rub)} против ${fmtRub(d.cpa_rub)} — проверьте мобильную вёрстку/корректировки.`;
    }
  }

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}
           onClick={() => setOpen(o => !o)}>
        <h3 className="card-title" style={{ margin: 0 }}>Срезы аудитории</h3>
        <span style={{ color: "var(--mute)", fontSize: 13 }}>{open ? "Свернуть" : "Показать"}</span>
      </div>

      {open && (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
            {BREAKDOWN_TABS.map(t => (
              <button key={t.dim}
                      className={`btn btn-sm ${dim === t.dim ? "btn-primary" : ""}`}
                      onClick={() => setDim(t.dim)}>{t.label}</button>
            ))}
          </div>

          <BreakdownAiPanel dim={dim} range={range} kind="audience"/>

          {mobileAlert && (
            <div style={{ marginBottom: 10, padding: "8px 10px", background: ZONE_TINT.red!.bg, color: ZONE_TINT.red!.ink, borderRadius: 6, fontSize: 12.5 }}>
              {mobileAlert}
            </div>
          )}

          {brkQ.isLoading ? <div style={{ display: "flex", justifyContent: "center", padding: "28px 12px" }}><Spinner label="Загрузка…"/></div> : data?.error ? (
            <div style={{ color: "var(--brick)", fontSize: 12.5, padding: 10 }}>Не удалось: {data.error}</div>
          ) : !data?.rows?.length ? (
            <div style={{ color: "var(--mute)", padding: 16, textAlign: "center" }}>Нет данных за период.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="tbl tbl-tight">
                <thead>
                  <tr>
                    <th>{BREAKDOWN_TABS.find(t => t.dim === dim)?.label}</th>
                    <th className="num">Показы</th>
                    <th className="num">Клики</th>
                    <th className="num">CTR</th>
                    <th className="num">Конв.</th>
                    <th className="num">CR</th>
                    <th className="num">Расход</th>
                    <th className="num">CPC</th>
                    <th className="num">CPA</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map(r => (
                    <tr key={r.key}>
                      <td>{r.label}</td>
                      <td className="num tabular">{fmtInt(r.impressions)}</td>
                      <td className="num tabular">{fmtInt(r.clicks)}</td>
                      <td className="num tabular" style={zoneCellStyle(r.zones?.ctr)}>{fmtPct(r.ctr)}</td>
                      <td className="num tabular">{fmtInt(r.conversions)}</td>
                      <td className="num tabular" style={zoneCellStyle(r.zones?.cr)}>{fmtPct(r.cr)}</td>
                      <td className="num tabular strong">{fmtRub(r.cost_rub)}</td>
                      <td className="num tabular">{fmtRub(r.avg_cpc_rub)}</td>
                      <td className="num tabular" style={zoneCellStyle(r.conversions > 0 ? r.zones?.cpa : undefined)}>{r.conversions > 0 ? fmtRub(r.cpa_rub) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function zoneCellStyle(zone?: MarketingZone): CSSProperties | undefined {
  const t = zone ? ZONE_TINT[zone] : null;
  return t ? { background: t.bg, color: t.ink } : undefined;
}

function Kpi({ label, value, zone, delta, accent }: {
  label: string; value: string; zone?: MarketingZone;
  delta?: { text: string; color: string } | null; accent?: boolean;
}) {
  const tint = zone ? ZONE_TINT[zone] : null;
  const edge = tint ? tint.dot : (accent ? "var(--brick)" : "transparent");
  return (
    <div className="mkt-kpi" style={{
      padding: "13px 15px", background: tint ? tint.bg : "#fff",
      border: "1px solid var(--line)", borderLeft: `3px solid ${edge}`, borderRadius: 12,
      boxShadow: "0 1px 2px rgba(20,20,20,0.04)",
    }}>
      <div style={{ fontSize: 10.5, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.5, display: "flex", alignItems: "center", gap: 5, minHeight: 13, lineHeight: 1.25 }}>
        {tint && <span style={{ width: 6, height: 6, borderRadius: "50%", background: tint.dot, display: "inline-block", flex: "0 0 auto" }}/>}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 6, fontFamily: "var(--serif)", letterSpacing: "-0.02em", color: tint ? tint.ink : "var(--ink)" }}>
        {value}
      </div>
      {delta && <div style={{ fontSize: 11, marginTop: 3, color: delta.color, fontWeight: 600 }}>{delta.text} <span style={{ color: "var(--mute)", fontWeight: 400 }}>vs пред.</span></div>}
    </div>
  );
}

const TT_STYLE: CSSProperties = {
  background: "#fff", border: "1px solid var(--line)", borderRadius: 8,
  fontSize: 12, color: "var(--ink)", boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
};

// --- Color picker в нашем стиле (HSV: SV-поле + hue-слайдер + hex) ---
function hsv2rgb(h: number, s: number, v: number): [number, number, number] {
  const c = v * s, x = c * (1 - Math.abs((h / 60) % 2 - 1)), m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; } else if (h < 120) { r = x; g = c; } else if (h < 180) { g = c; b = x; }
  else if (h < 240) { g = x; b = c; } else if (h < 300) { r = x; b = c; } else { r = c; b = x; }
  return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)];
}
function rgb2hex([r, g, b]: [number, number, number]): string {
  return "#" + [r, g, b].map(x => Math.max(0, Math.min(255, x)).toString(16).padStart(2, "0")).join("");
}
function hex2hsv(hex: string): { h: number; s: number; v: number } {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return { h: 140, s: 0.5, v: 0.7 };
  const n = parseInt(m[1], 16), r = (n >> 16) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  let h = 0;
  if (d) { if (mx === r) h = ((g - b) / d) % 6; else if (mx === g) h = (b - r) / d + 2; else h = (r - g) / d + 4; h *= 60; if (h < 0) h += 360; }
  return { h, s: mx ? d / mx : 0, v: mx };
}
function ColorPicker({ value, onChange }: { value: string; onChange: (hex: string) => void }) {
  const init = hex2hsv(value);
  const [h, setH] = useState(init.h);
  const [s, setS] = useState(init.s);
  const [v, setV] = useState(init.v);
  const [hex, setHex] = useState(value);
  const svRef = useRef<HTMLDivElement>(null);
  const hueRef = useRef<HTMLDivElement>(null);
  const emit = (nh: number, ns: number, nv: number) => { const hx = rgb2hex(hsv2rgb(nh, ns, nv)); setHex(hx); onChange(hx); };
  const clamp = (x: number) => Math.max(0, Math.min(1, x));
  const dragSV = (e: ReactPointerEvent) => {
    const move = (ev: PointerEvent | ReactPointerEvent) => {
      const r = svRef.current!.getBoundingClientRect();
      const ns = clamp(((ev as PointerEvent).clientX - r.left) / r.width);
      const nv = clamp(1 - ((ev as PointerEvent).clientY - r.top) / r.height);
      setS(ns); setV(nv); emit(h, ns, nv);
    };
    move(e); const up = () => { window.removeEventListener("pointermove", move as any); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move as any); window.addEventListener("pointerup", up);
  };
  const dragHue = (e: ReactPointerEvent) => {
    const move = (ev: PointerEvent | ReactPointerEvent) => {
      const r = hueRef.current!.getBoundingClientRect();
      const nh = clamp(((ev as PointerEvent).clientX - r.left) / r.width) * 360;
      setH(nh); emit(nh, s, v);
    };
    move(e); const up = () => { window.removeEventListener("pointermove", move as any); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move as any); window.addEventListener("pointerup", up);
  };
  const hueRgb = rgb2hex(hsv2rgb(h, 1, 1));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: 200 }} onClick={e => e.stopPropagation()}>
      <div ref={svRef} onPointerDown={dragSV}
           style={{ position: "relative", height: 120, borderRadius: 8, cursor: "crosshair", touchAction: "none",
                    background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hueRgb})` }}>
        <div style={{ position: "absolute", left: `${s * 100}%`, top: `${(1 - v) * 100}%`, transform: "translate(-50%,-50%)",
                      width: 12, height: 12, borderRadius: "50%", border: "2px solid #fff", boxShadow: "0 0 0 1px rgba(0,0,0,0.3)" }}/>
      </div>
      <div ref={hueRef} onPointerDown={dragHue}
           style={{ position: "relative", height: 12, borderRadius: 6, cursor: "pointer", touchAction: "none",
                    background: "linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)" }}>
        <div style={{ position: "absolute", left: `${h / 360 * 100}%`, top: "50%", transform: "translate(-50%,-50%)",
                      width: 14, height: 14, borderRadius: "50%", background: hueRgb, border: "2px solid #fff", boxShadow: "0 0 0 1px rgba(0,0,0,0.3)" }}/>
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <span style={{ width: 26, height: 26, borderRadius: 7, background: rgb2hex(hsv2rgb(h, s, v)), border: "1px solid rgba(0,0,0,0.15)", flex: "0 0 auto" }}/>
        <input className="input-sm" value={hex} onChange={e => { const val = e.target.value; setHex(val); if (/^#?[0-9a-f]{6}$/i.test(val)) { const hv = hex2hsv(val); setH(hv.h); setS(hv.s); setV(hv.v); onChange(val.startsWith("#") ? val : "#" + val); } }}
               style={{ flex: 1, fontFamily: "var(--mono)" }} placeholder="#4fae4e"/>
      </div>
    </div>
  );
}

const CHART_COLORS = ["#4fae4e", "#2459b8", "#e0883a", "#b23b30", "#7a6bff", "#1fa3a3"];

// Универсальный график: несколько серий, типы line/area/bar/combo (двойная ось), русифицированный тултип.
function ChartView({ chart, data, meta, height = 210 }: {
  chart: CustomChart; data: any[]; meta: (k: string) => { label: string; fmt: Fmt }; height?: number;
}) {
  const series = chart.series ?? [];
  const type = chart.type ?? "area";
  const hasRight = type === "combo" && series.some(s => s.axis === "R");
  const TipContent = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ ...TT_STYLE, padding: "8px 11px" }}>
        <div style={{ color: "var(--mute)", fontSize: 11, marginBottom: 5 }}>{label}</div>
        {payload.map((p: any) => (
          <div key={p.dataKey} style={{ display: "flex", gap: 12, justifyContent: "space-between", fontSize: 12, lineHeight: 1.6 }}>
            <span style={{ color: p.color, fontWeight: 600 }}>{meta(p.dataKey).label}</span>
            <strong>{fmtByFormat(Number(p.value), meta(p.dataKey).fmt)}</strong>
          </div>
        ))}
      </div>
    );
  };
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 6, right: 8, bottom: 0, left: -6 }}>
        <defs>
          {series.map((s, i) => (
            <linearGradient key={i} id={`grad-${chart.id}-${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color || CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0.22}/>
              <stop offset="100%" stopColor={s.color || CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0}/>
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false}/>
        <XAxis dataKey="d" tick={{ fontSize: 10, fill: "var(--mute)" }} tickLine={false} axisLine={{ stroke: "var(--line)" }} minTickGap={24}/>
        <YAxis yAxisId="L" tick={{ fontSize: 10, fill: "var(--mute)" }} tickLine={false} axisLine={false} width={46}/>
        {hasRight && <YAxis yAxisId="R" orientation="right" tick={{ fontSize: 10, fill: "var(--mute)" }} tickLine={false} axisLine={false} width={46}/>}
        <Tooltip content={<TipContent/>}/>
        {series.length > 1 && <Legend formatter={(v: any) => meta(String(v)).label} wrapperStyle={{ fontSize: 11 }}/>}
        {series.map((s, i) => {
          const col = s.color || CHART_COLORS[i % CHART_COLORS.length];
          const axisId = (type === "combo" && s.axis === "R") ? "R" : "L";
          const as = type === "combo" ? (s.as || "line") : type;
          if (as === "bar") return <Bar key={s.key} yAxisId={axisId} dataKey={s.key} name={s.key} fill={col} radius={[4, 4, 0, 0]}/>;
          if (as === "area") return <Area key={s.key} yAxisId={axisId} type="monotone" dataKey={s.key} name={s.key} stroke={col} strokeWidth={2.2} fill={`url(#grad-${chart.id}-${i})`} dot={false} activeDot={{ r: 4 }}/>;
          return <Line key={s.key} yAxisId={axisId} type="monotone" dataKey={s.key} name={s.key} stroke={col} strokeWidth={2.4} dot={false} activeDot={{ r: 4 }}/>;
        })}
      </ComposedChart>
    </ResponsiveContainer>
  );
}

const CHART_TYPE_LABEL: Record<string, string> = { line: "Линия", area: "Площадь", bar: "Столбцы", combo: "Комбо (2 оси)" };

function ChartCard({ chart, data, meta, onExpand, onHide }: {
  chart: CustomChart; data: any[]; meta: (k: string) => { label: string; fmt: Fmt }; onExpand?: () => void; onHide?: () => void;
}) {
  const series = chart.series ?? [];
  const first = series[0]?.key;
  const last = first && data.length ? Number(data[data.length - 1][first] ?? 0) : 0;
  return (
    <div className="card mkt-chart-card">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, gap: 8 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>{chart.title}</div>
          {series.length === 1 ? (
            <div style={{ fontSize: 21, fontWeight: 800, fontFamily: "var(--serif)", letterSpacing: "-0.02em", marginTop: 4 }}>
              {first ? fmtByFormat(last, meta(first).fmt) : "—"} <span style={{ fontSize: 11, fontWeight: 500, color: "var(--mute)" }}>посл. день</span>
            </div>
          ) : (
            <div style={{ fontSize: 11.5, color: "var(--ink-2)", marginTop: 4 }}>{series.map(s => meta(s.key).label).join(" · ")}</div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, flex: "0 0 auto" }}>
          {onExpand && (
            <button className="btn btn-sm" title="Развернуть на весь экран" onClick={onExpand}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
              </svg>
            </button>
          )}
          {onHide && (
            <button className="btn btn-sm" title="Скрыть график" onClick={onHide} style={{ color: "var(--mute)" }}>✕</button>
          )}
        </div>
      </div>
      <ChartView chart={chart} data={data} meta={meta} height={210}/>
    </div>
  );
}


// ===== Скаттер-квадранты по кампаниям (X × Y, размер = 3-я метрика) =====
const SCATTER_METRICS: { key: string; label: string; fmt: Fmt }[] = [
  { key: "cost_rub", label: "Расход ₽", fmt: "rub" }, { key: "conversions", label: "Конверсии", fmt: "int" },
  { key: "clicks", label: "Клики", fmt: "int" }, { key: "impressions", label: "Показы", fmt: "int" },
  { key: "ctr", label: "CTR %", fmt: "pct" }, { key: "cr", label: "CR %", fmt: "pct" },
  { key: "avg_cpc_rub", label: "CPC ₽", fmt: "rub" }, { key: "cpa_rub", label: "CPA ₽", fmt: "rub" },
  { key: "revenue", label: "Выручка ₽", fmt: "rub" }, { key: "romi", label: "ROMI %", fmt: "pct" },
  { key: "payback", label: "Окупаемость ×", fmt: "num2" },
];
function scatterVal(c: MarketingCampaignRow, key: string, crm?: { byCampaign: Record<string, { revenue?: number; calls?: number }> }): number {
  const e = crm?.byCampaign?.[String(c.direct_id)];
  if (key === "revenue") return e?.revenue ?? 0;
  if (key === "calls") return e?.calls ?? 0;
  if (key === "romi") return c.cost_rub > 0 ? ((e?.revenue ?? 0) - c.cost_rub) / c.cost_rub * 100 : 0;
  if (key === "payback") return c.cost_rub > 0 ? (e?.revenue ?? 0) / c.cost_rub : 0;
  return Number((c as any)[key] ?? 0);
}
function CampaignScatter({ campaigns, crm, onHide }: {
  campaigns: MarketingCampaignRow[];
  crm?: { byCampaign: Record<string, { revenue?: number; calls?: number }> };
  onHide?: () => void;
}) {
  const [xK, setXK] = useState("cost_rub");
  const [yK, setYK] = useState(crm ? "romi" : "cpa_rub");
  const [zK, setZK] = useState("conversions");
  const xM = SCATTER_METRICS.find(m => m.key === xK)!, yM = SCATTER_METRICS.find(m => m.key === yK)!, zM = SCATTER_METRICS.find(m => m.key === zK)!;
  const pts = campaigns.filter(c => c.cost_rub > 0 || c.clicks > 0).map(c => ({
    x: scatterVal(c, xK, crm), y: scatterVal(c, yK, crm), z: Math.max(scatterVal(c, zK, crm), 0.0001), name: c.name,
  }));
  const avg = (arr: number[]) => arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0;
  const avgX = avg(pts.map(p => p.x)), avgY = avg(pts.map(p => p.y));
  const qColor = (p: { x: number; y: number }) => p.y >= avgY ? (p.x >= avgX ? "#2e7d32" : "#66bb6a") : (p.x >= avgX ? "#c62828" : "#9e9e9e");
  const Tip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div style={{ ...TT_STYLE, padding: "8px 11px" }}>
        <div style={{ fontWeight: 700, fontSize: 12.5, marginBottom: 4, maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</div>
        <div style={{ fontSize: 12 }}>{xM.label}: <strong>{fmtByFormat(d.x, xM.fmt)}</strong></div>
        <div style={{ fontSize: 12 }}>{yM.label}: <strong>{fmtByFormat(d.y, yM.fmt)}</strong></div>
        <div style={{ fontSize: 12 }}>{zM.label}: <strong>{fmtByFormat(d.z, zM.fmt)}</strong></div>
      </div>
    );
  };
  const sel = (val: string, set: (v: string) => void, label: string) => (
    <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--mute)" }}>
      {label}
      <select className="input-sm" value={val} onChange={e => set(e.target.value)}>
        {SCATTER_METRICS.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
      </select>
    </label>
  );
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <h3 className="card-title" style={{ margin: 0 }}>Карта кампаний (квадранты)</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>{sel(xK, setXK, "X:")}{sel(yK, setYK, "Y:")}{sel(zK, setZK, "размер:")}{onHide && <button className="btn btn-sm" title="Скрыть" onClick={onHide} style={{ color: "var(--mute)" }}>✕</button>}</div>
      </div>
      {pts.length === 0 ? (
        <div style={{ color: "var(--mute)", padding: 20, textAlign: "center" }}>Нет кампаний с активностью за период.</div>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={440}>
            <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
              <CartesianGrid stroke="var(--line)" strokeDasharray="3 3"/>
              <XAxis type="number" dataKey="x" name={xM.label} tick={{ fontSize: 10, fill: "var(--mute)" }} tickFormatter={v => fmtByFormat(v, xM.fmt)}/>
              <YAxis type="number" dataKey="y" name={yM.label} tick={{ fontSize: 10, fill: "var(--mute)" }} tickFormatter={v => fmtByFormat(v, yM.fmt)} width={56}/>
              <ZAxis type="number" dataKey="z" range={[50, 520]} name={zM.label}/>
              <ReferenceLine x={avgX} stroke="var(--mute)" strokeDasharray="4 4"/>
              <ReferenceLine y={avgY} stroke="var(--mute)" strokeDasharray="4 4"/>
              <Tooltip content={<Tip/>} cursor={{ strokeDasharray: "3 3" }}/>
              <Scatter data={pts} fillOpacity={0.72}>
                {pts.map((p, i) => <Cell key={i} fill={qColor(p)}/>)}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11.5, color: "var(--mute)", marginTop: 8 }}>
            <span><span style={{ color: "#2e7d32" }}>●</span> масштабировать (высокий {yM.label} + объём)</span>
            <span><span style={{ color: "#66bb6a" }}>●</span> растить (высокий {yM.label})</span>
            <span><span style={{ color: "#c62828" }}>●</span> резать (низкий {yM.label} + дорого)</span>
            <span><span style={{ color: "#9e9e9e" }}>●</span> наблюдать</span>
            <span>· размер точки = {zM.label}, пунктир = средние</span>
          </div>
        </>
      )}
    </div>
  );
}

// Пончик: доли кампаний по выбранной аддитивной метрике (топ-8 + «Прочие»).
const DONUT_PALETTE = ["#4fae4e", "#2459b8", "#e0883a", "#b23b30", "#7a6bff", "#1fa3a3", "#d65db1", "#8d6e63", "#5c6bc0"];
function CampaignDonut({ campaigns, crm, onHide }: {
  campaigns: MarketingCampaignRow[];
  crm?: { byCampaign: Record<string, { revenue?: number; calls?: number }> };
  onHide?: () => void;
}) {
  const OPTS = SCATTER_METRICS.filter(m => ["cost_rub", "conversions", "clicks", "impressions", "revenue"].includes(m.key));
  const [metric, setMetric] = useState("cost_rub");
  const m = OPTS.find(x => x.key === metric) ?? OPTS[0];
  const vals = campaigns.map(c => ({ name: c.name, v: scatterVal(c, metric, crm) })).filter(x => x.v > 0).sort((a, b) => b.v - a.v);
  const top = vals.slice(0, 8);
  const rest = vals.slice(8).reduce((s, x) => s + x.v, 0);
  const data = rest > 0 ? [...top, { name: "Прочие", v: rest }] : top;
  const total = data.reduce((s, x) => s + x.v, 0) || 1;
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <h3 className="card-title" style={{ margin: 0 }}>Доли кампаний</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--mute)" }}>
            Метрика
            <select className="input-sm" value={metric} onChange={e => setMetric(e.target.value)}>
              {OPTS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </label>
          {onHide && <button className="btn btn-sm" title="Скрыть" onClick={onHide} style={{ color: "var(--mute)" }}>✕</button>}
        </div>
      </div>
      {data.length === 0 ? (
        <div style={{ color: "var(--mute)", padding: 20, textAlign: "center" }}>Нет данных за период.</div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 320px) 1fr", gap: 16, alignItems: "center" }}>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={data} dataKey="v" nameKey="name" cx="50%" cy="50%" innerRadius={62} outerRadius={100} paddingAngle={1.5}>
                {data.map((_, i) => <Cell key={i} fill={DONUT_PALETTE[i % DONUT_PALETTE.length]}/>)}
              </Pie>
              <Tooltip formatter={(v: any) => fmtByFormat(Number(v), m.fmt)} contentStyle={TT_STYLE}/>
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {data.map((d, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5 }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: DONUT_PALETTE[i % DONUT_PALETTE.length], flex: "0 0 auto" }}/>
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</span>
                <strong style={{ flex: "0 0 auto" }}>{fmtByFormat(d.v, m.fmt)}</strong>
                <span style={{ flex: "0 0 44px", textAlign: "right", color: "var(--mute)" }}>{(d.v / total * 100).toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Тепловая карта: неделя (столбцы) × день недели (строки), цвет = значение метрики за день.
function WeekdayHeatmap({ daily, onHide }: { daily: { date: string; impressions: number; clicks: number; cost_rub: number; conversions: number }[]; onHide?: () => void }) {
  const METS: { key: string; label: string; fmt: Fmt }[] = [
    { key: "cost_rub", label: "Расход ₽", fmt: "rub" }, { key: "clicks", label: "Клики", fmt: "int" },
    { key: "impressions", label: "Показы", fmt: "int" }, { key: "conversions", label: "Конверсии", fmt: "int" },
  ];
  const [mk, setMk] = useState("cost_rub");
  const m = METS.find(x => x.key === mk) ?? METS[0];
  const DOW = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];
  const cells = daily.map(d => {
    const dt = new Date(d.date + "T00:00:00");
    const dow = (dt.getDay() + 6) % 7;
    const monday = new Date(dt); monday.setDate(dt.getDate() - dow);
    return { date: d.date, v: Number((d as any)[mk] ?? 0), dow, wk: monday.toISOString().slice(0, 10) };
  });
  const weeks = Array.from(new Set(cells.map(c => c.wk))).sort();
  const byWk: Record<string, Record<number, { date: string; v: number }>> = {};
  cells.forEach(c => { (byWk[c.wk] ??= {})[c.dow] = { date: c.date, v: c.v }; });
  const max = Math.max(...cells.map(c => c.v), 1);
  const color = (v: number) => v <= 0 ? "var(--paper-2, #f1f1f4)" : `rgba(79,174,78,${(0.12 + 0.88 * (v / max)).toFixed(3)})`;
  const dowAvg = DOW.map((_, dow) => { const vs = cells.filter(c => c.dow === dow).map(c => c.v); return vs.length ? vs.reduce((s, v) => s + v, 0) / vs.length : 0; });
  const items: ReactNode[] = [<div key="corner"/>];
  weeks.forEach(wk => items.push(<div key={"h" + wk} style={{ fontSize: 9, color: "var(--mute)", textAlign: "center" }}>{wk.slice(8, 10)}.{wk.slice(5, 7)}</div>));
  DOW.forEach((dl, dow) => {
    items.push(<div key={"l" + dow} style={{ fontSize: 11, color: "var(--ink-2)", fontWeight: 600, paddingRight: 6, textAlign: "right" }}>{dl}</div>);
    weeks.forEach(wk => {
      const cell = byWk[wk]?.[dow];
      items.push(<div key={wk + dow} title={cell ? `${cell.date.slice(8, 10)}.${cell.date.slice(5, 7)} · ${m.label}: ${fmtByFormat(cell.v, m.fmt)}` : ""}
                      style={{ height: 30, borderRadius: 5, background: cell ? color(cell.v) : "transparent", border: cell ? "none" : "1px dashed var(--line)" }}/>);
    });
  });
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <h3 className="card-title" style={{ margin: 0 }}>Тепловая карта по дням недели</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--mute)" }}>
            Метрика
            <select className="input-sm" value={mk} onChange={e => setMk(e.target.value)}>
              {METS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </label>
          {onHide && <button className="btn btn-sm" title="Скрыть" onClick={onHide} style={{ color: "var(--mute)" }}>✕</button>}
        </div>
      </div>
      <div style={{ overflowX: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: `34px repeat(${weeks.length}, minmax(24px, 1fr))`, gap: 4, alignItems: "center" }}>{items}</div>
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
        {DOW.map((dl, i) => (
          <div key={dl} style={{ fontSize: 11, color: "var(--mute)" }}>{dl}: <strong style={{ color: "var(--ink-2)" }}>{fmtByFormat(dowAvg[i], m.fmt)}</strong></div>
        ))}
        <span style={{ fontSize: 11, color: "var(--mute)" }}>· среднее за день недели</span>
      </div>
    </div>
  );
}

function StateBadge({ state }: { state: string | null }) {
  if (!state) return <span style={{ fontSize: 11, color: "var(--mute)" }}>—</span>;
  const map: Record<string, { bg: string; color: string; label: string }> = {
    ON:        { bg: "#e8f5e9", color: "#2e7d32", label: "Работает" },
    SUSPENDED: { bg: "#fff8e1", color: "#f57f17", label: "Остановлена" },
    OFF:       { bg: "#fafafa", color: "#666",    label: "Выключена" },
    ARCHIVED:  { bg: "#fafafa", color: "#999",    label: "Архив" },
    ENDED:     { bg: "#fafafa", color: "#666",    label: "Завершена" },
  };
  const s = map[state] || { bg: "var(--paper-2)", color: "var(--ink-2)", label: state };
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 10,
      background: s.bg, color: s.color, fontSize: 11, fontWeight: 600,
    }}>{s.label}</span>
  );
}


// ===== Таблица кампаний: поиск по словам + настраиваемые столбцы =====
type CampCol = { key: string; label: string; num: boolean; always?: boolean; render: (c: MarketingCampaignRow) => ReactNode; zone?: (c: MarketingCampaignRow) => MarketingZone | undefined; strong?: boolean };

// Кастомные столбцы = формула из токенов: метрика → операция → метрика/число → …
// Вычисляется слева направо. Можно сколько угодно метрик и чисел.
type FOp = "+" | "-" | "*" | "/";
type FToken = { t: "m"; k: string } | { t: "o"; o: FOp } | { t: "n"; v: number };
type CustomCol = { id: string; label: string; fmt: Fmt; tokens: FToken[] };
const CC_METRICS: { key: string; label: string }[] = [
  { key: "impressions", label: "Показы" }, { key: "clicks", label: "Клики" },
  { key: "cost_rub", label: "Расход ₽" }, { key: "conversions", label: "Конверсии" },
  { key: "ctr", label: "CTR %" }, { key: "cr", label: "CR %" },
  { key: "avg_cpc_rub", label: "CPC ₽" }, { key: "cpa_rub", label: "CPA ₽" },
  { key: "sessions", label: "Визиты" }, { key: "bounce_rate", label: "% отказов" },
  { key: "avg_pageviews", label: "Глубина" },
];
const F_OPS: { o: FOp; sym: string; label: string }[] = [
  { o: "/", sym: "÷", label: "делить" },
  { o: "*", sym: "×", label: "умножить" },
  { o: "+", sym: "+", label: "сложить" },
  { o: "-", sym: "−", label: "вычесть" },
];
const opSym = (o: FOp) => F_OPS.find(x => x.o === o)?.sym ?? o;
// Back-compat: старый формат {a,op,b} → токены.
function migrateCol(raw: any): CustomCol {
  if (Array.isArray(raw?.tokens)) return raw as CustomCol;
  const { a, b, op, id, label, fmt } = raw || {};
  let tokens: FToken[] = a ? [{ t: "m", k: a }] : [];
  const pair = (o: FOp): FToken[] => [{ t: "m", k: a }, { t: "o", o }, { t: "m", k: b }];
  if (op === "ratio") tokens = [{ t: "m", k: a }, { t: "o", o: "/" }, { t: "m", k: b }, { t: "o", o: "*" }, { t: "n", v: 100 }];
  else if (op === "div") tokens = pair("/");
  else if (op === "mul") tokens = pair("*");
  else if (op === "sum") tokens = pair("+");
  else if (op === "sub") tokens = pair("-");
  return { id: id || String(Date.now()), label: label || "Метрика", fmt: fmt || "num2", tokens };
}
function formulaText(tokens: FToken[], label: (k: string) => string): string {
  return tokens.map(tk => tk.t === "m" ? label(tk.k) : tk.t === "n" ? String(tk.v) : opSym(tk.o)).join(" ");
}
const CAMPAIGN_COLUMNS: CampCol[] = [
  { key: "name", label: "Название", num: false, always: true, render: c => c.name },
  { key: "type", label: "Тип", num: false, render: c => <span style={{ fontSize: 11, color: "var(--mute)" }}>{c.type}</span> },
  { key: "state", label: "Статус", num: false, render: c => <StateBadge state={c.state}/> },
  { key: "impressions", label: "Показы", num: true, render: c => fmtInt(c.impressions) },
  { key: "clicks", label: "Клики", num: true, render: c => fmtInt(c.clicks) },
  { key: "ctr", label: "CTR", num: true, render: c => fmtPct(c.ctr), zone: c => c.zones?.ctr },
  { key: "cr", label: "CR (итого)", num: true, render: c => fmtPct(c.cr), zone: c => c.zones?.cr },
  { key: "cost_rub", label: "Расход с НДС", num: true, strong: true, render: c => fmtRub(c.cost_rub) },
  { key: "avg_cpc_rub", label: "CPC", num: true, render: c => fmtRub(c.avg_cpc_rub) },
  { key: "cpa_rub", label: "CPA", num: true, render: c => c.conversions > 0 ? fmtRub(c.cpa_rub) : "—", zone: c => c.conversions > 0 ? c.zones?.cpa : undefined },
  { key: "sessions", label: "Визиты", num: true, render: c => fmtInt(c.sessions ?? 0) },
  { key: "bounce_rate", label: "% отказов", num: true, render: c => fmtPct(c.bounce_rate ?? 0) },
  { key: "avg_pageviews", label: "Глубина", num: true, render: c => `${(c.avg_pageviews ?? 0).toFixed(2).replace(".", ",")}` },
];
// По умолчанию на дашборде Метрика-столбцы скрыты (компактнее); на аналитике — всё.
const DASH_HIDDEN_DEFAULT = ["sessions", "bounce_rate", "avg_pageviews"];

export type CampFilters = { search?: string; status?: string; type?: string };
export function CampaignsTable({ campaigns, loading, storageKey, title, headerExtra, defaultHidden = [], crm, convGoals = [], initialFilters, onSavePreset, presetActions, compact }: {
  campaigns: MarketingCampaignRow[]; loading?: boolean; storageKey: string; title: string;
  headerExtra?: ReactNode; defaultHidden?: string[];
  crm?: { stages: { stage_id: number; name: string }[]; byCampaign: Record<string, { total: number; stages: Record<string, number>; calls?: number; revenue?: number }> };
  convGoals?: { goal_id: number; name: string }[];
  initialFilters?: CampFilters; onSavePreset?: (f: CampFilters) => void; presetActions?: ReactNode; compact?: boolean;
}) {
  const [search, setSearch] = useState(initialFilters?.search ?? "");
  const [statusFilter, setStatusFilter] = useState(initialFilters?.status ?? "");
  const [typeFilter, setTypeFilter] = useState(initialFilters?.type ?? "");
  const [pickerOpen, setPickerOpen] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const drag = useRef({ down: false, startX: 0, startScroll: 0, moved: false });
  const [hidden, setHidden] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return new Set(raw ? JSON.parse(raw) : defaultHidden);
    } catch { return new Set(defaultHidden); }
  });
  const persist = (h: Set<string>) => { setHidden(new Set(h)); try { localStorage.setItem(storageKey, JSON.stringify([...h])); } catch { /* ignore */ } };
  const toggle = (k: string) => { const h = new Set(hidden); h.has(k) ? h.delete(k) : h.add(k); persist(h); };

  // --- Ширина столбцов (перетаскивание) ---
  const loadLS = (suffix: string): Record<string, any> => { try { return JSON.parse(localStorage.getItem(storageKey + suffix) || "{}"); } catch { return {}; } };
  const saveLS = (suffix: string, v: any) => { try { localStorage.setItem(storageKey + suffix, JSON.stringify(v)); } catch { /* ignore */ } };
  const defW = (col: CampCol) => col.key === "name" ? 240 : col.num ? 110 : 130;
  const [colW, setColW] = useState<Record<string, number>>(() => loadLS("_w"));
  const widthOf = (col: CampCol) => colW[col.key] ?? defW(col);
  const resizing = useRef<{ key: string; startX: number; startW: number } | null>(null);
  const onResizeDown = (e: ReactPointerEvent, col: CampCol) => {
    e.preventDefault(); e.stopPropagation();
    resizing.current = { key: col.key, startX: e.clientX, startW: widthOf(col) };
    const move = (ev: PointerEvent) => {
      const r = resizing.current; if (!r) return;
      const w = Math.max(60, Math.round(r.startW + (ev.clientX - r.startX)));
      setColW(prev => ({ ...prev, [r.key]: w }));
    };
    const up = () => {
      resizing.current = null;
      setColW(prev => { saveLS("_w", prev); return prev; });
      window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };

  // --- Палитра (пометка цветом столбцов/ячеек) ---
  const PALETTE = ["#ffd9d9", "#ffe9b0", "#cdeecd", "#cfe2ff", "#ecd6ff", "#ffe0c2"];
  const [paint, setPaint] = useState<string | null>(null);   // активная кисть; "erase" — стереть
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [colMarks, setColMarks] = useState<Record<string, string>>(() => loadLS("_colmark"));
  const [cellMarks, setCellMarks] = useState<Record<string, string>>(() => loadLS("_cellmark"));
  const paintCol = (key: string) => setColMarks(prev => { const n = { ...prev }; if (paint === "erase" || n[key] === paint) delete n[key]; else if (paint) n[key] = paint; saveLS("_colmark", n); return n; });
  const paintCell = (id: number, key: string) => setCellMarks(prev => { const ck = `${id}:${key}`; const n = { ...prev }; if (paint === "erase" || n[ck] === paint) delete n[ck]; else if (paint) n[ck] = paint; saveLS("_cellmark", n); return n; });
  const cellBg = (id: number, key: string): string | undefined => cellMarks[`${id}:${key}`] ?? colMarks[key];
  const clearMarks = () => { setColMarks({}); setCellMarks({}); saveLS("_colmark", {}); saveLS("_cellmark", {}); };

  // Кастомные столбцы (A op B), сохраняются в localStorage.
  const [customCols, setCustomCols] = useState<CustomCol[]>(() => {
    try { return (JSON.parse(localStorage.getItem(storageKey + "_customcols") || "[]") as any[]).map(migrateCol); } catch { return []; }
  });
  const saveCustomCols = (next: CustomCol[]) => { setCustomCols(next); saveLS("_customcols", next); };
  const [ccTokens, setCcTokens] = useState<FToken[]>([]);
  const [ccLabel, setCcLabel] = useState("");
  const [ccFmt, setCcFmt] = useState<Fmt>("num2");
  const [ccNum, setCcNum] = useState("");
  // Открыть конструктор на редактирование конкретной кастомной метрики (из попапа столбцов или из списка).
  const editCustomMetric = (cc: CustomCol) => {
    setCcLabel(cc.label); setCcTokens(cc.tokens.map(t => ({ ...t }))); setCcFmt(cc.fmt);
    setEditingCcId(cc.id); setMetricBuilderOpen(true); setPickerOpen(false);
  };
  const [sort, setSort] = useState<{ key: string; dir: 1 | -1 } | null>(null);  // клик по заголовку = сортировка
  const [colOrder, setColOrder] = useState<string[]>(() => { try { return JSON.parse(localStorage.getItem(storageKey + "_colorder") || "[]"); } catch { return []; } });
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);  // подсветка цели при перетаскивании столбца
  const [metricBuilderOpen, setMetricBuilderOpen] = useState(false);  // модалка «Создать метрику»
  const [editingCcId, setEditingCcId] = useState<string | null>(null);  // редактируем существующую метрику
  // Авто-метрики по этапам: CR (шаг-к-шагу) + стоимость этапа (расход÷дошедшие). Как авто-карточки. Один раз.
  useEffect(() => {
    if (!crm?.stages?.length) return;
    const seedKey = storageKey + "_crseed2";
    try { if (localStorage.getItem(seedKey)) return; } catch { return; }
    setCustomCols(prev => {
      const existing = new Set(prev.map(c => c.id));
      const seeds: CustomCol[] = crm.stages.flatMap((st, i) => {
        const denom: FToken = i === 0 ? { t: "m", k: "clicks" } : { t: "m", k: `crm:${crm.stages[i - 1].stage_id}` };
        return [
          { id: `crstage-${st.stage_id}`, label: `CR в ${st.name}`, fmt: "pct" as Fmt,
            tokens: [{ t: "m", k: `crm:${st.stage_id}` }, { t: "o", o: "/" }, denom, { t: "o", o: "*" }, { t: "n", v: 100 }] as FToken[] },
          { id: `coststage-${st.stage_id}`, label: `Стоимость ${st.name}`, fmt: "rub" as Fmt,
            tokens: [{ t: "m", k: "cost_rub" }, { t: "o", o: "/" }, { t: "m", k: `crm:${st.stage_id}` }] as FToken[] },
        ];
      }).filter(s => !existing.has(s.id));
      if (!seeds.length) return prev;
      const next = [...prev, ...seeds];
      saveLS("_customcols", next);
      return next;
    });
    try { localStorage.setItem(seedKey, "1"); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crm?.stages?.length, storageKey]);

  // Динамические CRM-столбцы по кампаниям (Тёплый/Квал/Договор…) — passed-through из Amo.
  const crmCols: CampCol[] = (crm?.stages ?? []).map(st => ({
    key: `crm:${st.stage_id}`, label: st.name, num: true,
    render: (c: MarketingCampaignRow) => fmtInt(crm!.byCampaign[String(c.direct_id)]?.stages?.[String(st.stage_id)] ?? 0),
  }));
  // Звонки из Amo + конверсия из звонка в первый этап воронки.
  const firstStage = crm?.stages?.[0];
  const callCols: CampCol[] = crm ? [
    { key: "calls", label: "Звонки", num: true, render: (c: MarketingCampaignRow) => fmtInt(crm.byCampaign[String(c.direct_id)]?.calls ?? 0) },
    ...(firstStage ? [{
      key: "call_cr", label: `Конв. звонок→${firstStage.name}`, num: true,
      render: (c: MarketingCampaignRow) => {
        const e = crm.byCampaign[String(c.direct_id)];
        const calls = e?.calls ?? 0;
        const reached = e?.stages?.[String(firstStage.stage_id)] ?? 0;
        return calls > 0 ? fmtPct(reached / calls * 100) : "—";
      },
    } as CampCol] : []),
  ] : [];
  // Финансовые метрики из Amo: выручка + ROMI/доля/окупаемость (выручка ÷ расход Директа).
  const lastStage = crm?.stages?.[crm.stages.length - 1];
  const wonOf = (c: MarketingCampaignRow) => lastStage ? (crm!.byCampaign[String(c.direct_id)]?.stages?.[String(lastStage.stage_id)] ?? 0) : 0;
  const revCols: CampCol[] = crm ? [
    { key: "revenue", label: "Выручка", num: true, strong: true, render: (c: MarketingCampaignRow) => fmtRub(crm.byCampaign[String(c.direct_id)]?.revenue ?? 0) },
    { key: "avg_check", label: "Средний чек", num: true, render: (c: MarketingCampaignRow) => { const won = wonOf(c); return won > 0 ? fmtRub((crm.byCampaign[String(c.direct_id)]?.revenue ?? 0) / won) : "—"; } },
    { key: "romi", label: "ROMI", num: true, render: (c: MarketingCampaignRow) => { const rev = crm.byCampaign[String(c.direct_id)]?.revenue ?? 0; return c.cost_rub > 0 ? fmtPct((rev - c.cost_rub) / c.cost_rub * 100) : "—"; } },
    { key: "mkt_share", label: "Доля марк. в выручке", num: true, render: (c: MarketingCampaignRow) => { const rev = crm.byCampaign[String(c.direct_id)]?.revenue ?? 0; return rev > 0 ? fmtPct(c.cost_rub / rev * 100) : "—"; } },
    { key: "payback", label: "Окупаемость", num: true, render: (c: MarketingCampaignRow) => { const rev = crm.byCampaign[String(c.direct_id)]?.revenue ?? 0; return c.cost_rub > 0 ? (rev / c.cost_rub).toFixed(2).replace(".", ",") + "×" : "—"; } },
  ] : [];
  // Конверсии по каждой цели Я.Директа (с подписью) + «итого», как карточки.
  let convCols: CampCol[];
  if (convGoals.length > 0) {
    convCols = convGoals.map(g => ({
      key: `conv:${g.goal_id}`, label: `Конв · ${g.name}`, num: true,
      render: (c: MarketingCampaignRow) => fmtInt(c.conv_by_goal?.[String(g.goal_id)] ?? 0),
    }));
    if (convGoals.length > 1) {
      convCols.push({
        key: "conv:total", label: "Конв · итого", num: true,
        render: (c: MarketingCampaignRow) => fmtInt(convGoals.reduce((s, g) => s + (c.conv_by_goal?.[String(g.goal_id)] ?? 0), 0)),
      });
    }
  } else {
    convCols = [{ key: "conversions", label: "Конв.", num: true, render: (c: MarketingCampaignRow) => fmtInt(c.conversions) }];
  }
  // вставляем конверсии сразу после CTR
  const baseCols = [...CAMPAIGN_COLUMNS];
  const ctrIdx = baseCols.findIndex(c => c.key === "ctr");
  const withConv = ctrIdx >= 0
    ? [...baseCols.slice(0, ctrIdx + 1), ...convCols, ...baseCols.slice(ctrIdx + 1)]
    : [...baseCols, ...convCols];
  // Метрики, доступные для конструктора — сгруппированы по источнику данных.
  const METRIC_GROUPS: { source: string; items: { key: string; label: string }[] }[] = [
    { source: "Яндекс.Директ", items: CC_METRICS },
    ...(crm?.stages?.length ? [{ source: "CRM — этапы воронки", items: [...crm.stages.map(st => ({ key: `crm:${st.stage_id}`, label: st.name })), { key: "calls", label: "Звонки" }, { key: "revenue", label: "Выручка ₽" }, { key: "avg_check", label: "Средний чек ₽" }] }] : []),
    ...(convGoals.length ? [{ source: "Цели Я.Метрики", items: convGoals.map(g => ({ key: `conv:${g.goal_id}`, label: g.name })) }] : []),
  ];
  const metricLabel = (key: string): string => { for (const g of METRIC_GROUPS) { const m = g.items.find(i => i.key === key); if (m) return m.label; } return key; };
  const metricNum = (c: MarketingCampaignRow, key: string): number => {
    if (key === "calls") return crm?.byCampaign[String(c.direct_id)]?.calls ?? 0;
    if (key === "revenue") return crm?.byCampaign[String(c.direct_id)]?.revenue ?? 0;
    if (key === "romi") { const rev = crm?.byCampaign[String(c.direct_id)]?.revenue ?? 0; return c.cost_rub > 0 ? (rev - c.cost_rub) / c.cost_rub * 100 : 0; }
    if (key === "mkt_share") { const rev = crm?.byCampaign[String(c.direct_id)]?.revenue ?? 0; return rev > 0 ? c.cost_rub / rev * 100 : 0; }
    if (key === "payback") { const rev = crm?.byCampaign[String(c.direct_id)]?.revenue ?? 0; return c.cost_rub > 0 ? rev / c.cost_rub : 0; }
    if (key === "avg_check") { const won = wonOf(c); return won > 0 ? (crm?.byCampaign[String(c.direct_id)]?.revenue ?? 0) / won : 0; }
    if (key.startsWith("crm:")) return crm?.byCampaign[String(c.direct_id)]?.stages?.[key.slice(4)] ?? 0;
    if (key === "conv:total") return convGoals.reduce((s, g) => s + (c.conv_by_goal?.[String(g.goal_id)] ?? 0), 0);
    if (key.startsWith("conv:")) return c.conv_by_goal?.[key.slice(5)] ?? 0;
    const raw = (c as any)[key];
    return typeof raw === "number" ? raw : 0;
  };
  const evalTokens = (c: MarketingCampaignRow, toks: FToken[]): number => {
    if (!toks.length) return 0;
    const valOf = (tk: FToken) => tk.t === "m" ? metricNum(c, tk.k) : tk.t === "n" ? tk.v : 0;
    let acc = valOf(toks[0]);
    for (let i = 1; i + 1 < toks.length; i += 2) {
      const op = toks[i], rhs = toks[i + 1];
      if (op.t !== "o") break;
      const v = valOf(rhs);
      acc = op.o === "+" ? acc + v : op.o === "-" ? acc - v : op.o === "*" ? acc * v : (v ? acc / v : 0);
    }
    return acc;
  };
  const computeCC = (c: MarketingCampaignRow, cc: CustomCol): number => evalTokens(c, cc.tokens);
  const customColDefs: CampCol[] = customCols.map(cc => ({
    key: `cc:${cc.id}`, label: cc.label, num: true,
    render: (c: MarketingCampaignRow) => fmtByFormat(computeCC(c, cc), cc.fmt),
  }));
  const ALL_COLS = [...withConv, ...crmCols, ...callCols, ...revCols, ...customColDefs];
  // Порядок столбцов (перетаскивание за шапку). always-столбец (Название) всегда первый.
  const visibleCols = ALL_COLS.filter(c => c.always || !hidden.has(c.key));
  const ordRest = visibleCols.filter(c => !c.always).sort((a, b) => {
    const ia = colOrder.indexOf(a.key), ib = colOrder.indexOf(b.key);
    return (ia < 0 ? 9999 : ia) - (ib < 0 ? 9999 : ib);
  });
  const cols = [...visibleCols.filter(c => c.always), ...ordRest];
  const reorderCol = (fromKey: string, toKey: string) => {
    if (fromKey === toKey) return;
    const base = ALL_COLS.filter(c => !c.always).map(c => c.key);
    let order = (colOrder.length ? colOrder : base).filter(k => base.includes(k));
    base.forEach(k => { if (!order.includes(k)) order.push(k); });
    const from = order.indexOf(fromKey), to = order.indexOf(toKey);
    if (from < 0 || to < 0) return;
    order.splice(to, 0, order.splice(from, 1)[0]);
    setColOrder(order); saveLS("_colorder", order);
  };
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  const STATE_LABELS: Record<string, string> = { ON: "Работает", SUSPENDED: "Остановлена", OFF: "Выключена", ARCHIVED: "Архив", ENDED: "Завершена", CONVERTED: "Сконвертирована" };
  const statusOpts = Array.from(new Set(campaigns.map(c => c.state).filter(Boolean) as string[])).sort();
  const typeOpts = Array.from(new Set(campaigns.map(c => c.type).filter(Boolean) as string[])).sort();
  const sortValue = (c: MarketingCampaignRow, key: string): number | string => {
    if (key === "name") return (c.name || "").toLowerCase();
    if (key === "type") return (c.type || "").toLowerCase();
    if (key === "state") return (c.state || "").toLowerCase();
    if (key === "calls") return crm?.byCampaign[String(c.direct_id)]?.calls ?? 0;
    if (key.startsWith("crm:")) return crm?.byCampaign[String(c.direct_id)]?.stages?.[key.slice(4)] ?? 0;
    if (key === "conv:total") return convGoals.reduce((s, g) => s + (c.conv_by_goal?.[String(g.goal_id)] ?? 0), 0);
    if (key.startsWith("conv:")) return c.conv_by_goal?.[key.slice(5)] ?? 0;
    if (key.startsWith("cc:")) { const cc = customCols.find(x => `cc:${x.id}` === key); return cc ? computeCC(c, cc) : 0; }
    return metricNum(c, key);
  };
  const rows = campaigns
    .filter(c => words.every(w => (c.name || "").toLowerCase().includes(w)))
    .filter(c => !statusFilter || c.state === statusFilter)
    .filter(c => !typeFilter || c.type === typeFilter)
    .sort((a, b) => {
      if (!sort) return b.cost_rub - a.cost_rub;
      const av = sortValue(a, sort.key), bv = sortValue(b, sort.key);
      if (typeof av === "string" || typeof bv === "string") return String(av).localeCompare(String(bv)) * sort.dir;
      return (av - bv) * sort.dir;
    });
  const toggleSort = (key: string) => setSort(s => s?.key === key ? { key, dir: (s.dir * -1) as 1 | -1 } : { key, dir: -1 });
  // Агрегаты для строки ИТОГО.
  const T = (() => {
    let imp = 0, clk = 0, cost = 0, conv = 0, sess = 0, bnc = 0, pv = 0, revenue = 0, calls = 0;
    const stages: Record<string, number> = {}, goals: Record<string, number> = {};
    rows.forEach(c => {
      imp += c.impressions; clk += c.clicks; cost += c.cost_rub; conv += c.conversions; sess += c.sessions || 0;
      bnc += (c.bounce_rate || 0) / 100 * (c.sessions || 0); pv += (c.avg_pageviews || 0) * (c.sessions || 0);
      const e = crm?.byCampaign[String(c.direct_id)];
      revenue += e?.revenue || 0; calls += e?.calls || 0;
      crm?.stages.forEach(s => { stages[String(s.stage_id)] = (stages[String(s.stage_id)] || 0) + (e?.stages?.[String(s.stage_id)] || 0); });
      convGoals.forEach(g => { goals[String(g.goal_id)] = (goals[String(g.goal_id)] || 0) + (c.conv_by_goal?.[String(g.goal_id)] || 0); });
    });
    return { imp, clk, cost, conv, sess, bnc, pv, revenue, calls, stages, goals };
  })();
  const totalCell = (key: string): string => {
    if (key === "name") return `ИТОГО · ${rows.length}`;
    if (key === "impressions") return fmtInt(T.imp);
    if (key === "clicks") return fmtInt(T.clk);
    if (key === "cost_rub") return fmtRub(T.cost);
    if (key === "conversions") return fmtInt(T.conv);
    if (key === "sessions") return fmtInt(T.sess);
    if (key === "revenue") return fmtRub(T.revenue);
    if (key === "calls") return fmtInt(T.calls);
    if (key === "ctr") return fmtPct(T.imp ? T.clk / T.imp * 100 : 0);
    if (key === "cr") return fmtPct(T.clk ? T.conv / T.clk * 100 : 0);
    if (key === "avg_cpc_rub") return fmtRub(T.clk ? T.cost / T.clk : 0);
    if (key === "cpa_rub") return T.conv ? fmtRub(T.cost / T.conv) : "—";
    if (key === "bounce_rate") return fmtPct(T.sess ? T.bnc / T.sess * 100 : 0);
    if (key === "avg_pageviews") return T.sess ? (T.pv / T.sess).toFixed(2).replace(".", ",") : "—";
    if (key === "romi") return T.cost ? fmtPct((T.revenue - T.cost) / T.cost * 100) : "—";
    if (key === "mkt_share") return T.revenue ? fmtPct(T.cost / T.revenue * 100) : "—";
    if (key === "payback") return T.cost ? (T.revenue / T.cost).toFixed(2).replace(".", ",") + "×" : "—";
    if (key === "avg_check") { const won = lastStage ? (T.stages[String(lastStage.stage_id)] || 0) : 0; return won > 0 ? fmtRub(T.revenue / won) : "—"; }
    if (key === "call_cr") { const fs = crm?.stages?.[0]; const r = fs ? (T.stages[String(fs.stage_id)] || 0) : 0; return T.calls ? fmtPct(r / T.calls * 100) : "—"; }
    if (key === "conv:total") return fmtInt(convGoals.reduce((s, g) => s + (T.goals[String(g.goal_id)] || 0), 0));
    if (key.startsWith("crm:")) return fmtInt(T.stages[key.slice(4)] || 0);
    if (key.startsWith("conv:")) return fmtInt(T.goals[key.slice(5)] || 0);
    // Кастомная метрика: считаем формулу от ИТОГО-значений каждой метрики (а не сумму строк).
    if (key.startsWith("cc:")) {
      const cc = customCols.find(x => `cc:${x.id}` === key);
      if (!cc) return "";
      const tv = (k: string): number => {
        if (k === "cost_rub" || k === "cost") return T.cost;
        if (k === "ctr") return T.imp ? T.clk / T.imp * 100 : 0;
        if (k === "cr") return T.clk ? T.conv / T.clk * 100 : 0;
        if (k === "avg_cpc_rub") return T.clk ? T.cost / T.clk : 0;
        if (k === "cpa_rub") return T.conv ? T.cost / T.conv : 0;
        if (k === "bounce_rate") return T.sess ? T.bnc / T.sess * 100 : 0;
        if (k === "avg_pageviews") return T.sess ? T.pv / T.sess : 0;
        if (k === "romi") return T.cost ? (T.revenue - T.cost) / T.cost * 100 : 0;
        if (k === "mkt_share") return T.revenue ? T.cost / T.revenue * 100 : 0;
        if (k === "payback") return T.cost ? T.revenue / T.cost : 0;
        if (k === "avg_check") { const won = lastStage ? (T.stages[String(lastStage.stage_id)] || 0) : 0; return won ? T.revenue / won : 0; }
        if (k === "revenue") return T.revenue;
        if (k === "calls") return T.calls;
        if (k === "impressions") return T.imp;
        if (k === "clicks") return T.clk;
        if (k === "conversions") return T.conv;
        if (k === "sessions") return T.sess;
        if (k.startsWith("crm:")) return T.stages[k.slice(4)] || 0;
        if (k === "conv:total") return convGoals.reduce((s, g) => s + (T.goals[String(g.goal_id)] || 0), 0);
        if (k.startsWith("conv:")) return T.goals[k.slice(5)] || 0;
        return 0;
      };
      const toks = cc.tokens;
      if (!toks.length) return "—";
      const vof = (t: FToken) => t.t === "m" ? tv(t.k) : t.t === "n" ? t.v : 0;
      let acc = vof(toks[0]);
      for (let i = 1; i + 1 < toks.length; i += 2) { const op = toks[i], rhs = toks[i + 1]; if (op.t !== "o") break; const v = vof(rhs); acc = op.o === "+" ? acc + v : op.o === "-" ? acc - v : op.o === "*" ? acc * v : (v ? acc / v : 0); }
      return fmtByFormat(acc, cc.fmt);
    }
    return "";
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <style>{`
        .camp-table { border-collapse: separate !important; border-spacing: 0 !important; }
        .camp-table th, .camp-table td { border-bottom: 1px solid var(--line) !important; }
        .camp-table thead th { box-shadow: 0 1px 0 var(--line); }
      `}</style>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
        <h3 className="card-title" style={{ margin: 0 }}>{title} <span style={{ color: "var(--mute)", fontWeight: 400, fontSize: 13 }}>· {rows.length}</span></h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", position: "relative" }}>
          <input className="input-sm" placeholder="Поиск по словам в названии…" value={search}
                 onChange={e => setSearch(e.target.value)} style={{ minWidth: 200 }}/>
          {statusOpts.length > 0 && (
            <select className="input-sm" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} title="Статус кампании">
              <option value="">Все статусы</option>
              {statusOpts.map(s => <option key={s} value={s}>{STATE_LABELS[s] || s}</option>)}
            </select>
          )}
          {typeOpts.length > 0 && (
            <select className="input-sm" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} title="Тип кампании">
              <option value="">Все типы</option>
              {typeOpts.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
          <span style={{ position: "relative", display: "inline-flex" }}>
          <button className="btn btn-sm" onClick={() => setPickerOpen(o => !o)} title="Настройка столбцов"
                  style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
          {pickerOpen && (
            <>
              <div onClick={() => setPickerOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }}/>
              <div style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 41, background: "var(--card)",
                            border: "1px solid var(--line)", borderRadius: 12, padding: 12, boxShadow: "0 6px 20px rgba(0,0,0,0.14)",
                            width: 300, maxHeight: 460, overflowY: "auto" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>Столбцы</div>
                <div style={{ fontSize: 11, color: "var(--mute)", marginBottom: 6 }}>Тяни за ⠿ — поменять порядок. Галочка — показать/скрыть.</div>
                {ALL_COLS.filter(c => !c.always).sort((a, b) => { const ia = colOrder.indexOf(a.key), ib = colOrder.indexOf(b.key); return (ia < 0 ? 9999 : ia) - (ib < 0 ? 9999 : ib); }).map(c => {
                  const isCustom = c.key.startsWith("cc:");
                  return (
                    <div key={c.key} draggable
                         onDragStart={e => { e.dataTransfer.setData("colmove", c.key); e.dataTransfer.effectAllowed = "move"; }}
                         onDragOver={e => { if (e.dataTransfer.types.includes("colmove")) { e.preventDefault(); setDragOverKey(c.key); } }}
                         onDragLeave={() => setDragOverKey(k => (k === c.key ? null : k))}
                         onDrop={e => { const src = e.dataTransfer.getData("colmove"); if (src) { e.preventDefault(); reorderCol(src, c.key); } setDragOverKey(null); }}
                         onDragEnd={() => setDragOverKey(null)}
                         style={{ display: "flex", alignItems: "center", gap: 6, borderRadius: 6, padding: "1px 2px", background: dragOverKey === c.key ? "#eaf6ee" : undefined }}>
                      <span style={{ cursor: "grab", color: "var(--mute)", opacity: 0.5, fontSize: 13, flex: "0 0 auto" }}>⠿</span>
                      <label style={{ flex: 1, display: "flex", gap: 8, alignItems: "center", fontSize: 13, padding: "4px 2px", cursor: "pointer", minWidth: 0 }}>
                        <input type="checkbox" checked={!hidden.has(c.key)} onChange={() => toggle(c.key)}/>
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.label}</span>
                      </label>
                      {isCustom && (() => { const cc = customCols.find(x => `cc:${x.id}` === c.key); return cc ? (<>
                        <button onClick={() => editCustomMetric(cc)} title="Редактировать метрику"
                                style={{ border: "none", background: "transparent", color: "var(--ink-2)", cursor: "pointer", fontSize: 13, lineHeight: 1, flex: "0 0 auto" }}>✎</button>
                        <button onClick={() => saveCustomCols(customCols.filter(x => x.id !== cc.id))} title="Удалить столбец"
                                style={{ border: "none", background: "transparent", color: "#b23b30", cursor: "pointer", fontSize: 15, lineHeight: 1, flex: "0 0 auto" }}>×</button>
                      </>) : null; })()}
                    </div>
                  );
                })}
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--line)" }}>
                  <button className="btn btn-sm btn-primary" style={{ width: "100%" }}
                          onClick={() => { setPickerOpen(false); setMetricBuilderOpen(true); }}>+ Создать метрику</button>
                </div>
              </div>
            </>
          )}
          </span>
          <button className="btn btn-sm" onClick={() => setPaletteOpen(o => !o)}
                  title="Палитра — помечать столбцы и ячейки цветом"
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, borderColor: paint ? "var(--brick)" : undefined }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="13.5" cy="6.5" r=".5" fill="currentColor"/><circle cx="17.5" cy="10.5" r=".5" fill="currentColor"/>
              <circle cx="8.5" cy="7.5" r=".5" fill="currentColor"/><circle cx="6.5" cy="12.5" r=".5" fill="currentColor"/>
              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.9 0 1.6-.7 1.6-1.6 0-.4-.2-.8-.4-1.1-.3-.3-.4-.7-.4-1.1 0-.9.7-1.6 1.6-1.6H18c2.2 0 4-1.8 4-4 0-4.4-4.5-8.6-10-8.6z"/>
            </svg>
            Палитра
            {paint && paint !== "erase" && <span style={{ width: 11, height: 11, borderRadius: 3, background: paint, border: "1px solid rgba(0,0,0,0.15)" }}/>}
          </button>
          {paletteOpen && (
            <>
              <div onClick={() => setPaletteOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }}/>
              <div style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 41, background: "var(--card)",
                            border: "1px solid var(--line)", borderRadius: 12, padding: 12, boxShadow: "0 6px 20px rgba(0,0,0,0.14)", minWidth: 220 }}>
                <div style={{ fontSize: 12, color: "var(--mute)", marginBottom: 8 }}>Выбери цвет, потом кликай по заголовку столбца или по ячейкам</div>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 10 }}>
                  {PALETTE.map(col => (
                    <button key={col} onClick={() => setPaint(col)} title="Кисть"
                            style={{ width: 26, height: 26, borderRadius: 8, background: col, cursor: "pointer",
                                     border: paint === col ? "2px solid var(--ink)" : "1px solid rgba(0,0,0,0.12)" }}/>
                  ))}
                  <button onClick={() => setPaint("erase")} title="Ластик — убрать пометку"
                          style={{ width: 28, height: 28, borderRadius: 9, background: "#fff", cursor: "pointer", display: "grid", placeItems: "center",
                                   color: "var(--ink-2)", border: paint === "erase" ? "2px solid var(--brick)" : "1px solid var(--line)",
                                   boxShadow: "0 1px 2px rgba(20,20,20,0.05)" }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M8.5 21H21"/><path d="M3.6 14.1l6.3-6.3a2 2 0 0 1 2.83 0l4.66 4.66a2 2 0 0 1 0 2.83l-4.7 4.7H8.5l-4.9-4.9a2 2 0 0 1 0-2.99z"/>
                    </svg>
                  </button>
                  <button onClick={() => setShowPicker(p => !p)} title="Свой цвет"
                          style={{ position: "relative", width: 28, height: 28, borderRadius: 9, cursor: "pointer", padding: 0,
                                   border: (paint && !PALETTE.includes(paint) && paint !== "erase") || showPicker ? "2px solid var(--brick)" : "1px solid var(--line)",
                                   boxShadow: "0 1px 2px rgba(20,20,20,0.05)", overflow: "hidden",
                                   background: paint && !PALETTE.includes(paint) && paint !== "erase" ? paint : "conic-gradient(from 210deg, #ff5c5c, #ffb02e, #ffe24d, #5fd06a, #2ab7ff, #7a6bff, #ff6bd6, #ff5c5c)" }}>
                    <span style={{ position: "absolute", right: 2, bottom: 2, width: 12, height: 12, borderRadius: "50%", background: "#fff",
                                   display: "grid", placeItems: "center", boxShadow: "0 0 0 1px rgba(0,0,0,0.12)", fontSize: 11, lineHeight: 1, color: "#333", fontWeight: 700 }}>+</span>
                  </button>
                </div>
                {showPicker && (
                  <div style={{ marginBottom: 10 }}>
                    <ColorPicker value={paint && paint !== "erase" ? paint : "#4fae4e"} onChange={c => setPaint(c)}/>
                  </div>
                )}
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <button className="btn btn-sm" onClick={() => setPaint(null)} disabled={!paint}
                          style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 3l18 18"/><path d="M9.06 11.9l8.07-8.06a2.85 2.85 0 0 1 4.03 4.03l-5.1 5.1"/><path d="M7 15a3 3 0 0 0-3 3c0 1.3-2.5 1.5-2 2 1.1 1.1 2.5 2 4 2a3 3 0 0 0 3-3"/>
                    </svg>
                    Выкл кисть
                  </button>
                  <button className="btn btn-sm" onClick={clearMarks} style={{ color: "#b23b30", display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                    </svg>
                    Очистить всё
                  </button>
                </div>
              </div>
            </>
          )}
          {onSavePreset && (
            <button className="btn btn-sm" title="Сохранить текущие фильтры в отдельную таблицу"
                    onClick={() => onSavePreset({ search, status: statusFilter, type: typeFilter })}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
              Сохранить фильтр
            </button>
          )}
          {presetActions}
          {headerExtra}
        </div>
      </div>
      {loading ? <div style={{ display: "flex", justifyContent: "center", padding: "28px 12px" }}><Spinner label="Загрузка…"/></div> : rows.length === 0 ? (
        <div style={{ color: "var(--mute)", padding: 20, textAlign: "center" }}>
          {campaigns.length === 0 ? "Кампаний нет. Нажмите «Синхронизировать»." : "Ничего не найдено по запросу."}
        </div>
      ) : (
        <div
          ref={scrollRef}
          className="camp-scroll"
          style={{ overflowX: "auto", overflowY: "auto", maxHeight: compact ? undefined : "72vh", cursor: paint ? BRUSH_CURSOR : "grab", scrollbarWidth: "thin" }}
          onDragOver={e => {
            const el = scrollRef.current; if (!el || !e.dataTransfer.types.includes("colmove")) return;
            const r = el.getBoundingClientRect(); const edge = 70;
            if (e.clientX < r.left + edge) el.scrollLeft -= 16;
            else if (e.clientX > r.right - edge) el.scrollLeft += 16;
          }}
          onPointerDown={e => {
            const el = scrollRef.current; if (!el || paint) return;
            if ((e.target as HTMLElement).closest("button,a,input,select,label,.col-resize,th")) return;
            drag.current = { down: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false };
            el.style.cursor = "grabbing"; el.style.userSelect = "none";
          }}
          onPointerMove={e => {
            const el = scrollRef.current; if (!el || !drag.current.down) return;
            const dx = e.clientX - drag.current.startX;
            if (Math.abs(dx) > 3) drag.current.moved = true;
            el.scrollLeft = drag.current.startScroll - dx;
          }}
          onPointerUp={() => { const el = scrollRef.current; if (el && !paint) { el.style.cursor = "grab"; el.style.userSelect = ""; } drag.current.down = false; }}
          onPointerLeave={() => { const el = scrollRef.current; if (el && !paint) { el.style.cursor = "grab"; el.style.userSelect = ""; } drag.current.down = false; }}
        >
          <table className="tbl tbl-tight camp-table" style={{ tableLayout: "fixed", width: "100%", minWidth: cols.reduce((s, c) => s + widthOf(c), 0) }}>
            <colgroup>
              {cols.map(c => <col key={c.key} style={{ width: widthOf(c) }}/>)}
            </colgroup>
            <thead>
              <tr>
                {cols.map((c, i) => (
                  <th key={c.key} className={c.num ? "num" : undefined}
                      draggable={!paint && !c.always}
                      onDragStart={e => { e.dataTransfer.setData("colmove", c.key); e.dataTransfer.effectAllowed = "move"; }}
                      onDragOver={e => { if (e.dataTransfer.types.includes("colmove") && !c.always) { e.preventDefault(); setDragOverKey(c.key); } }}
                      onDragLeave={() => setDragOverKey(k => (k === c.key ? null : k))}
                      onDrop={e => { const src = e.dataTransfer.getData("colmove"); if (src && !c.always) { e.preventDefault(); reorderCol(src, c.key); } setDragOverKey(null); }}
                      onDragEnd={() => setDragOverKey(null)}
                      onClick={() => paint ? paintCol(c.key) : toggleSort(c.key)}
                      title={paint ? "Покрасить столбец" : (c.always ? "Клик — сортировать" : "Клик — сортировать · тяни — переставить")}
                      style={{ position: "sticky", top: 0, zIndex: i === 0 ? 3 : 2, cursor: paint ? BRUSH_CURSOR : (c.always ? "pointer" : "grab"),
                               background: colMarks[c.key] || "var(--card)",
                               boxShadow: dragOverKey === c.key ? "inset 3px 0 0 var(--brick)" : (i === 0 ? "2px 0 0 var(--line)" : undefined),
                               ...(i === 0 ? { left: 0 } : {}) }}>
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block", paddingRight: 14 }}>
                      {c.label}
                      {sort?.key === c.key && <span style={{ color: "var(--brick)", fontWeight: 700 }}> {sort.dir === -1 ? "↓" : "↑"}</span>}
                    </span>
                    <span className="col-resize" onPointerDown={e => onResizeDown(e, c)}
                          style={{ position: "absolute", top: 0, right: 0, width: 9, height: "100%", cursor: "col-resize", touchAction: "none" }}/>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(c => (
                <tr key={c.direct_id}>
                  {cols.map((col, i) => {
                    const bg = cellBg(c.direct_id, col.key);
                    return (
                      <td key={col.key} className={col.num ? `num tabular${col.strong ? " strong" : ""}` : undefined}
                          onClick={() => paint && paintCell(c.direct_id, col.key)}
                          style={{ ...(col.zone && !bg ? zoneCellStyle(col.zone(c)) : {}),
                                   ...(bg ? { background: bg } : {}), cursor: paint ? BRUSH_CURSOR : undefined,
                                   ...(i === 0 ? { position: "sticky", left: 0, zIndex: 1, background: bg || "var(--card)", boxShadow: "2px 0 0 var(--line)" } : {}),
                                   overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {col.render(c)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                {cols.map((col, i) => (
                  <td key={col.key} className={col.num ? "num tabular strong" : "strong"}
                      style={{ position: "sticky", bottom: 0, zIndex: i === 0 ? 4 : 2, background: "var(--paper-2, #f4f5f7)",
                               fontWeight: 800, fontSize: 14, padding: "12px 12px", borderTop: "2px solid var(--line)", whiteSpace: "nowrap",
                               ...(i === 0 ? { left: 0, zIndex: 5, boxShadow: "2px 0 0 var(--line)" } : {}) }}>
                    {totalCell(col.key)}
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {metricBuilderOpen && (() => {
        const last = ccTokens[ccTokens.length - 1];
        const endsWithValue = ccTokens.length > 0 && last.t !== "o";   // последняя — метрика/число
        const OP_ORDER: FOp[] = ["/", "*", "+", "-"];
        // Клик по метрике/числу СРАЗУ присоединяет её знаком (÷ по умолч. для метрик, × для чисел).
        const addMetric = (k: string) => setCcTokens(endsWithValue ? [...ccTokens, { t: "o", o: "/" }, { t: "m", k }] : [...ccTokens, { t: "m", k }]);
        const addNum = () => { const v = parseFloat(ccNum.replace(",", ".")); if (isNaN(v)) return; setCcTokens(endsWithValue ? [...ccTokens, { t: "o", o: "*" }, { t: "n", v }] : [...ccTokens, { t: "n", v }]); setCcNum(""); };
        const cycleOp = (i: number) => setCcTokens(ccTokens.map((t, j) => (j === i && t.t === "o") ? { t: "o", o: OP_ORDER[(OP_ORDER.indexOf(t.o) + 1) % 4] } : t));
        const swapValues = (a: number, b: number) => { if (a === b || ccTokens[a]?.t === "o" || ccTokens[b]?.t === "o") return; const n = [...ccTokens]; [n[a], n[b]] = [n[b], n[a]]; setCcTokens(n); };
        const removeLast = () => setCcTokens(ccTokens.slice(0, (ccTokens.length >= 2 && last.t !== "o" && ccTokens[ccTokens.length - 2].t === "o") ? -2 : -1));
        const addCol = () => {
          if (!ccLabel.trim() || !ccTokens.length) return;
          const col = { id: editingCcId || String(Date.now()), label: ccLabel.trim(), fmt: ccFmt, tokens: ccTokens };
          saveCustomCols(editingCcId ? customCols.map(x => x.id === editingCcId ? col : x) : [...customCols, col]);
          setCcLabel(""); setCcTokens([]); setCcNum(""); setEditingCcId(null);
        };
        const preview = rows[0] ? fmtByFormat(evalTokens(rows[0], ccTokens), ccFmt) : "—";
        const chip: CSSProperties = { display: "inline-block", padding: "5px 11px", borderRadius: 8, fontSize: 12.5, fontWeight: 500, cursor: "grab", userSelect: "none", boxShadow: "0 1px 2px rgba(20,20,20,0.04)", border: "1px solid var(--line)", background: "var(--card)" };
        return (
          <Modal title="Создать метрику" onClose={() => setMetricBuilderOpen(false)} width={960}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 28, minHeight: 420 }}>
              {/* ЛЕВО: метрики по источникам */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>Метрики по источникам</div>
                <div style={{ fontSize: 11.5, color: "var(--mute)", marginBottom: 10 }}>Кликай метрики — каждая присоединится знаком. Можно сколько угодно.</div>
                {METRIC_GROUPS.map(g => (
                  <div key={g.source} style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-2)", marginBottom: 6 }}>{g.source}</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {g.items.map(m => (
                        <span key={m.key} draggable
                              onDragStart={e => e.dataTransfer.setData("text/plain", m.key)}
                              onClick={() => addMetric(m.key)}
                              style={chip}>{m.label}</span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {/* ПРАВО: формула */}
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>Формула</div>
                <input className="input-sm" placeholder="Название (напр. CR в договор)" value={ccLabel}
                       onChange={e => setCcLabel(e.target.value)} style={{ width: "100%", marginBottom: 10 }}/>
                {/* Строка формулы — токены */}
                <div onDragOver={e => e.preventDefault()}
                     onDrop={e => { e.preventDefault(); const k = e.dataTransfer.getData("text/plain"); if (k) addMetric(k); }}
                     style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", minHeight: 46, padding: "8px 10px", borderRadius: 10,
                              border: "2px dashed var(--line)", background: "var(--paper-2, #f6f7f9)", marginBottom: 8 }}>
                  {ccTokens.length === 0 && <span style={{ fontSize: 12, color: "var(--mute)" }}>Пусто — кликни метрику слева</span>}
                  {ccTokens.map((tk, i) => tk.t === "o" ? (
                    <button key={i} onClick={() => cycleOp(i)} title="Клик — сменить операцию (÷ × + −)"
                            style={{ padding: "4px 11px", borderRadius: 8, fontSize: 15, fontWeight: 800, lineHeight: 1, cursor: "pointer",
                                     background: "#fff5e0", color: "#b8860b", border: "1px solid #f0d98c" }}>{opSym(tk.o)}</button>
                  ) : (
                    <span key={i} draggable
                          onDragStart={e => { e.dataTransfer.setData("reorder", String(i)); e.dataTransfer.effectAllowed = "move"; }}
                          onDragOver={e => { if (e.dataTransfer.types.includes("reorder")) e.preventDefault(); }}
                          onDrop={e => { const src = e.dataTransfer.getData("reorder"); if (src !== "") { e.preventDefault(); e.stopPropagation(); swapValues(Number(src), i); } }}
                          title="Перетащи на другую метрику, чтобы поменять местами"
                          style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 8px", borderRadius: 8, fontSize: 12.5, fontWeight: 700, cursor: "grab",
                              background: tk.t === "m" ? "#eaf6ee" : "#e7f0ff", color: tk.t === "m" ? "#1f7a3f" : "#2459b8" }}>
                      <span style={{ opacity: 0.4, fontSize: 11, cursor: "grab" }}>⠿</span>
                      {tk.t === "m" ? metricLabel(tk.k) : tk.v}
                    </span>
                  ))}
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                    <input value={ccNum} onChange={e => setCcNum(e.target.value)}
                           onKeyDown={e => { if (e.key === "Enter" && ccNum.trim()) addNum(); }}
                           placeholder="+ число" title="Любое число: 100, 0.5, 1.5 и т.д."
                           style={{ width: 78, border: "1px dashed var(--line)", borderRadius: 8, padding: "4px 8px", fontSize: 12.5, background: "transparent", outline: "none", color: "var(--ink)" }}/>
                    {ccNum.trim() && <button onClick={addNum} title="Добавить число"
                            style={{ border: "none", background: "var(--brick)", color: "#fff", borderRadius: 6, width: 22, height: 22, cursor: "pointer", fontSize: 15, lineHeight: 1, padding: 0 }}>+</button>}
                  </span>
                  {ccTokens.length > 0 && (
                    <button onClick={removeLast} title="Удалить последнее"
                            style={{ marginLeft: "auto", border: "none", background: "transparent", color: "var(--mute)", cursor: "pointer", fontSize: 14 }}>⌫</button>
                  )}
                </div>
                <div style={{ height: 12 }}/>
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <select className="input-sm" value={ccFmt} onChange={e => setCcFmt(e.target.value as Fmt)} style={{ flex: 1, minWidth: 0 }}>
                    <option value="int">Число</option><option value="num2">Число (2 знака)</option>
                    <option value="rub">Рубли ₽</option><option value="pct">Проценты %</option>
                  </select>
                  <button className="btn btn-sm btn-primary" disabled={!ccLabel.trim() || !ccTokens.length} onClick={addCol} style={{ flex: "0 0 auto" }}>{editingCcId ? "Сохранить" : "Добавить столбец"}</button>
                  {editingCcId && <button className="btn btn-sm" onClick={() => { setEditingCcId(null); setCcLabel(""); setCcTokens([]); setCcNum(""); }} style={{ flex: "0 0 auto" }}>Отмена</button>}
                </div>
                <div style={{ fontSize: 12.5, color: "var(--ink-2)", padding: "9px 11px", borderRadius: 10, background: "var(--paper-2, #f6f7f9)", marginBottom: 14 }}>
                  Пример (по первой кампании): <strong style={{ color: "var(--ink)" }}>{preview}</strong>
                </div>
                {customCols.length > 0 && (
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--mute)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 8 }}>Созданные метрики</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {customCols.map(cc => (
                        <div key={cc.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, padding: "7px 10px", borderRadius: 9, border: `1px solid ${editingCcId === cc.id ? "var(--brick)" : "var(--line)"}` }}>
                          <span style={{ flex: 1, minWidth: 0 }}><strong>{cc.label}</strong> <span style={{ color: "var(--mute)" }}>= {formulaText(cc.tokens, metricLabel)}</span></span>
                          <button onClick={() => editCustomMetric(cc)} title="Редактировать"
                                  style={{ border: "none", background: "transparent", color: "var(--ink-2)", cursor: "pointer", fontSize: 13, lineHeight: 1 }}>✎</button>
                          <button onClick={() => { if (editingCcId === cc.id) { setEditingCcId(null); setCcLabel(""); setCcTokens([]); } saveCustomCols(customCols.filter(x => x.id !== cc.id)); }} title="Удалить"
                                  style={{ border: "none", background: "transparent", color: "#b23b30", cursor: "pointer", fontSize: 15, lineHeight: 1 }}>×</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}

export { BreakdownPanel, CleanupPanel, CampaignScatter, CampaignDonut, DASH_HIDDEN_DEFAULT };
export default MarketingPage;
